import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import CategorieSelector, { chargerCategories, invaliderCacheCategories } from '../components/CategorieSelector';
import SelectHumanise from '../components/SelectHumanise';
import {
  STATUTS_PAIE as STATUTS_PAIE_DICT, SOURCES_PAIE as SOURCES_PAIE_DICT, NIVEAUX_CONFIANCE_PAIE,
  TYPES_VALEUR_PARAMETRE, DOMAINES_PAIE, UNITES_PAIE, TYPES_RUBRIQUE as TYPES_RUBRIQUE_DICT,
  FAMILLES_RUBRIQUE as FAMILLES_RUBRIQUE_DICT, MODES_CALCUL_RUBRIQUE as MODES_CALCUL_RUBRIQUE_DICT,
  TYPES_DOCUMENT_JUSTIFICATIF, ARRONDIS_GRILLE, FAMILLES_PROFESSIONNELLES,
  humaniserStatut, humaniserSource, humaniserNiveauConfiance, humaniserDomaine, humaniserUnite,
  humaniserTypeRubrique, humaniserFamilleRubrique, humaniserModeCalcul, humaniserTypeDocument, humaniserArrondi,
  humaniserFamilleProfessionnelle,
  COULEUR_INTERDIT_DE_CALCUL,
} from '../constants/libellesHumains';
import {
  Pencil, Trash2, Factory, Building2, Users,
  CheckCircle, XCircle, Tag, LogOut, Clock, Wallet, Bell,
  Upload, Download, FileSpreadsheet, RefreshCw, AlertCircle,
  ChevronDown, ChevronUp, ShieldPlus, CreditCard, Settings, ListTree, AlertTriangle, Paperclip, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
// Suivi « prochain acteur » (versions du dossier salarial du centre de validation).
import { LigneSuivi } from '../components/suivi/SuiviDemande';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const VERT      = '#1B5E20';
const ROUGE     = '#C62828';
const GRIS_BORD = '#E0E0E0';

const inp = {
  padding: '7px 10px',
  border: `1px solid ${GRIS_BORD}`,
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};

const SECTIONS = [
  { group: 'RÉFÉRENTIELS' },
  { id: 'sites',         label: 'Sites',                   Icone: Factory   },
  { id: 'departements',  label: 'Départements',            Icone: Building2 },
  { id: 'services',      label: 'Services',                Icone: Users     },
  { id: 'referentiels-a-completer', label: 'Référentiels à compléter', Icone: AlertTriangle },
  { id: 'classification-categories', label: 'Classification des catégories', Icone: Tag },
  { group: 'EMPLOYÉS' },
  { id: 'statuts',       label: 'Statuts employé',         Icone: Tag       },
  { id: 'motifs',        label: 'Motifs de départ',        Icone: LogOut    },
  { group: 'RETRAITE' },
  { id: 'ages',          label: 'Ages & alertes retraite', Icone: Clock     },
  { id: 'provisions',    label: 'Provisions retraite',     Icone: Wallet    },
  { group: 'ASSURANCE MALADIE' },
  { id: 'ipm',           label: 'IPM',                     Icone: ShieldPlus },
  { group: 'PRÊTS SOGAS' },
  { id: 'prets-sogas',   label: 'Prêts SOGAS',             Icone: CreditCard },
  { group: 'PAIE (RH-PAIE-2)' },
  { id: 'centre-validation', label: 'Centre de validation',  Icone: CheckCircle },
  { id: 'paie-parametres', label: 'Paramètres de paie',    Icone: Settings },
  { id: 'paie-rubriques',  label: 'Rubriques de paie',     Icone: ListTree },
  { id: 'grille-salariale', label: 'Grille salariale',     Icone: Wallet },
  { group: 'SYSTÈME' },
  { id: 'notifications', label: 'Notifications',           Icone: Bell      },
  { group: 'DONNÉES' },
  { id: 'import-export', label: 'Import / Export',         Icone: Upload    },
];

// ── Composants définis HORS du parent (react-component-rules) ──────────────

const BtnEdit = ({ onClick }) => (
  <button onClick={onClick} title="Modifier" style={{
    background: 'none', border: `1px solid #BBE0BB`, color: VERT,
    borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13,
    marginRight: 6, display: 'inline-flex', alignItems: 'center',
  }}><Pencil size={13} /></button>
);

const BtnDel = ({ onClick }) => (
  <button onClick={onClick} title="Supprimer" style={{
    background: 'none', border: '1px solid #FFCDD2', color: ROUGE,
    borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13,
    display: 'inline-flex', alignItems: 'center',
  }}><Trash2 size={13} /></button>
);

// Module 0 — archivage logique : une entité utilisée n'est jamais supprimée, elle est archivée
// (plus proposée pour une nouvelle affectation, l'historique reste intact).
const BtnArchive = ({ actif, onArchiver, onReactiver }) => (
  <button onClick={actif === false ? onReactiver : onArchiver} title={actif === false ? 'Réactiver' : 'Archiver'} style={{
    background: 'none', border: `1px solid ${GRIS_BORD}`, color: '#555',
    borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, marginRight: 4,
  }}>{actif === false ? 'Réactiver' : 'Archiver'}</button>
);

const BadgeArchive = ({ actif }) => (actif === false
  ? <span style={{ fontSize: 11, color: '#666', background: '#EEEEEE', padding: '2px 8px', borderRadius: 12 }}>Archivé</span>
  : null);

const BtnSave = ({ onClick }) => (
  <button onClick={onClick} style={{
    background: VERT, color: '#fff', border: 'none',
    borderRadius: 6, padding: '5px 14px', cursor: 'pointer',
    fontWeight: 600, fontSize: 12, marginRight: 6,
  }}>Sauvegarder</button>
);

const BtnCancel = ({ onClick }) => (
  <button onClick={onClick} style={{
    background: 'none', border: `1px solid ${GRIS_BORD}`, color: '#666',
    borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12,
  }}>Annuler</button>
);

const Card = ({ titre, Icone, children }) => (
  <div style={{
    background: '#fff', borderRadius: 12, border: `1px solid ${GRIS_BORD}`,
    padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${GRIS_BORD}`,
    }}>
      {Icone && <Icone size={18} color={VERT} />}
      <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>{titre}</span>
    </div>
    {children}
  </div>
);

const SectionVide = ({ titre }) => (
  <div style={{
    background: '#fff', borderRadius: 12, border: `1px solid ${GRIS_BORD}`,
    padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>{titre}</div>
    <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 8 }}>
      Cette section sera disponible dans une prochaine session.
    </div>
  </div>
);

// ── Couleurs prédéfinies pour les statuts ───────────────
const COULEURS_PRESET = [
  '#1B5E20', '#888780', '#378ADD', '#EF9F27', '#E24B4A', '#7F77DD', '#2C2C2A',
];

const Toggle = ({ on, onClick }) => (
  <div onClick={onClick} style={{
    width: 34, height: 20, borderRadius: 999, cursor: 'pointer', flexShrink: 0,
    background: on ? VERT : '#CBD5E1', position: 'relative', transition: 'background 150ms',
  }}>
    <div style={{
      position: 'absolute', top: 2,
      left: on ? 16 : 2,
      width: 16, height: 16, borderRadius: '50%',
      background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      transition: 'left 150ms',
    }} />
  </div>
);

// ── StatutsSection — défini HORS de ParametresPage ─────
const StatutsSection = () => {
  const [statuts, setStatuts]     = useState([]);
  const [editItem, setEditItem]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ code: '', label: '', couleur: '#888780', est_actif: true, est_archive: false });
  const [localMsg, setLocalMsg]   = useState(null);
  const [confirmPending, setConfirmPending] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const res = await axios.get('/api/parametres/statuts');
      const data = res.data?.data;
      setStatuts(Array.isArray(data) ? data : (data?.rows || []));
    } catch (e) { console.error(e); }
  };

  const showMsg = (texte, type = 'ok') => {
    setLocalMsg({ texte, type });
    setTimeout(() => setLocalMsg(null), 3000);
  };

  const ajouter = async () => {
    if (!form.code || !form.label) return showMsg('Code et label requis', 'err');
    try {
      await axios.post('/api/parametres/statuts', form);
      setForm({ code: '', label: '', couleur: '#888780', est_actif: true, est_archive: false });
      setShowForm(false);
      charger();
      showMsg('Statut ajouté');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const sauvegarder = async () => {
    if (!editItem.label) return showMsg('Label requis', 'err');
    try {
      await axios.put(`/api/parametres/statuts/${editItem.id}`, editItem);
      setEditItem(null);
      charger();
      showMsg('Statut modifié');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const supprimer = (id) => {
    setConfirmPending({ titre: 'Supprimer ce statut ?', message: 'Cette action est irréversible.', action: 'Supprimer', onConfirm: async () => {
      try { await axios.delete(`/api/parametres/statuts/${id}`); charger(); showMsg('Statut supprimé'); }
      catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
    }});
  };

  const ColorPicker = ({ selected, onChange }) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {COULEURS_PRESET.map(c => (
        <div key={c} onClick={() => onChange(c)} style={{
          width: 20, height: 20, borderRadius: 4, background: c, cursor: 'pointer',
          border: selected === c ? '2px solid #333' : '2px solid transparent',
          flexShrink: 0,
        }} />
      ))}
    </div>
  );

  return (
    <>
      {localMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: localMsg.type === 'err' ? '#FFEBEE' : '#E8F5E9',
          color: localMsg.type === 'err' ? ROUGE : VERT,
          border: `1px solid ${localMsg.type === 'err' ? '#FFCDD2' : '#C8E6C9'}`,
          borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center',
        }}>
          {localMsg.type === 'err'
            ? <XCircle size={14} style={{ marginRight: 6 }} />
            : <CheckCircle size={14} style={{ marginRight: 6 }} />}
          {localMsg.texte}
        </div>
      )}

      {confirmPending && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{background:'#fff',borderRadius:14,padding:'24px 28px',maxWidth:380,width:'calc(100% - 32px)',boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}><div style={{fontSize:16,fontWeight:700,color:'#1A1A1A',marginBottom:8}}>{confirmPending.titre}</div><div style={{fontSize:13,color:'#666',marginBottom:24}}>{confirmPending.message}</div><div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button onClick={()=>setConfirmPending(null)} style={{padding:'10px 18px',border:'1px solid #e0e0e0',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>Annuler</button><button onClick={()=>{confirmPending.onConfirm();setConfirmPending(null);}} style={{padding:'10px 18px',border:'none',borderRadius:8,background:ROUGE,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>{confirmPending.action}</button></div></div></div>)}

      <Card titre="Statuts employé" Icone={Tag}>
        {/* En-tête + bouton ajouter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>États possibles d'un employé · {statuts.length} statut(s)</span>
          <button onClick={() => { setShowForm(!showForm); setEditItem(null); }} style={{
            background: VERT, color: '#fff', border: 'none', borderRadius: 6,
            padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
          }}>+ Ajouter</button>
        </div>

        {/* Formulaire d'ajout */}
        {showForm && (
          <div style={{
            background: '#F8F9FA', borderRadius: 8, padding: '14px 16px',
            marginBottom: 16, border: `1px solid ${GRIS_BORD}`,
          }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <input placeholder="Code (ex: CDI)" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                style={{ ...inp, width: 110 }} />
              <input placeholder="Label affiché" value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                style={{ ...inp, flex: 1, minWidth: 130 }} />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <ColorPicker selected={form.couleur} onChange={c => setForm({ ...form, couleur: c })} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <Toggle on={form.est_actif} onClick={() => setForm({ ...form, est_actif: !form.est_actif })} />
                Actif
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <Toggle on={form.est_archive} onClick={() => setForm({ ...form, est_archive: !form.est_archive })} />
                Archivé
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <BtnSave onClick={ajouter} />
                <BtnCancel onClick={() => setShowForm(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Liste des statuts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {statuts.map(s => {
            const enEdition = editItem?.id === s.id;
            return (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 8,
                background: enEdition ? '#F1F8E9' : '#F8F9FA',
                border: `1px solid ${enEdition ? '#AED581' : GRIS_BORD}`,
              }}>
                {enEdition ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input value={editItem.label}
                        onChange={e => setEditItem({ ...editItem, label: e.target.value })}
                        style={{ ...inp, flex: 1, minWidth: 120 }} />
                      <ColorPicker selected={editItem.couleur} onChange={c => setEditItem({ ...editItem, couleur: c })} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                        <Toggle on={editItem.est_actif} onClick={() => setEditItem({ ...editItem, est_actif: !editItem.est_actif })} />
                        Actif
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                        <Toggle on={editItem.est_archive} onClick={() => setEditItem({ ...editItem, est_archive: !editItem.est_archive })} />
                        Archivé
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                      <BtnSave onClick={sauvegarder} />
                      <BtnCancel onClick={() => setEditItem(null)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.couleur, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</span>
                      <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{s.code}</span>
                      {s.est_actif   && <span style={{ background: '#E8F5E9', color: VERT,    fontSize: 11, padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>Actif</span>}
                      {s.est_archive && <span style={{ background: '#F5F5F5', color: '#888',  fontSize: 11, padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>Archivé</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <BtnEdit onClick={() => { setEditItem({ ...s }); setShowForm(false); }} />
                      {s.modifiable
                        ? <BtnDel onClick={() => supprimer(s.id)} />
                        : <span style={{ fontSize: 11, color: '#94a3b8', padding: '0 6px' }}>système</span>
                      }
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
};

// ── MotifsSection — défini HORS de ParametresPage ──────
const MotifsSection = () => {
  const [motifs, setMotifs]       = useState([]);
  const [editItem, setEditItem]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ code: '', label: '', couleur: '#888780', actif: true });
  const [localMsg, setLocalMsg]   = useState(null);
  const [confirmPending, setConfirmPending] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const res = await axios.get('/api/parametres/motifs');
      const data = res.data?.data;
      setMotifs(Array.isArray(data) ? data : (data?.rows || []));
    } catch (e) { console.error(e); }
  };

  const showMsg = (texte, type = 'ok') => {
    setLocalMsg({ texte, type });
    setTimeout(() => setLocalMsg(null), 3000);
  };

  const ajouter = async () => {
    if (!form.code || !form.label) return showMsg('Code et label requis', 'err');
    try {
      await axios.post('/api/parametres/motifs', form);
      setForm({ code: '', label: '', couleur: '#888780', actif: true });
      setShowForm(false);
      charger();
      showMsg('Motif ajouté');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const sauvegarder = async () => {
    if (!editItem.label) return showMsg('Label requis', 'err');
    try {
      await axios.put(`/api/parametres/motifs/${editItem.id}`, editItem);
      setEditItem(null);
      charger();
      showMsg('Motif modifié');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const supprimer = (id) => {
    setConfirmPending({ titre: 'Supprimer ce motif ?', message: 'Cette action est irréversible.', action: 'Supprimer', onConfirm: async () => {
      try { await axios.delete(`/api/parametres/motifs/${id}`); charger(); showMsg('Motif supprimé'); }
      catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
    }});
  };

  const ColorPicker = ({ selected, onChange }) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {COULEURS_PRESET.map(c => (
        <div key={c} onClick={() => onChange(c)} style={{
          width: 20, height: 20, borderRadius: 4, background: c, cursor: 'pointer',
          border: selected === c ? '2px solid #333' : '2px solid transparent',
          flexShrink: 0,
        }} />
      ))}
    </div>
  );

  return (
    <>
      {localMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: localMsg.type === 'err' ? '#FFEBEE' : '#E8F5E9',
          color: localMsg.type === 'err' ? ROUGE : VERT,
          border: `1px solid ${localMsg.type === 'err' ? '#FFCDD2' : '#C8E6C9'}`,
          borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center',
        }}>
          {localMsg.type === 'err'
            ? <XCircle size={14} style={{ marginRight: 6 }} />
            : <CheckCircle size={14} style={{ marginRight: 6 }} />}
          {localMsg.texte}
        </div>
      )}

      {confirmPending && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{background:'#fff',borderRadius:14,padding:'24px 28px',maxWidth:380,width:'calc(100% - 32px)',boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}><div style={{fontSize:16,fontWeight:700,color:'#1A1A1A',marginBottom:8}}>{confirmPending.titre}</div><div style={{fontSize:13,color:'#666',marginBottom:24}}>{confirmPending.message}</div><div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button onClick={()=>setConfirmPending(null)} style={{padding:'10px 18px',border:'1px solid #e0e0e0',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>Annuler</button><button onClick={()=>{confirmPending.onConfirm();setConfirmPending(null);}} style={{padding:'10px 18px',border:'none',borderRadius:8,background:ROUGE,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>{confirmPending.action}</button></div></div></div>)}

      <Card titre="Motifs de départ" Icone={LogOut}>
        {/* En-tête + bouton ajouter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>Raisons de fin de contrat · {motifs.length} motif(s)</span>
          <button onClick={() => { setShowForm(!showForm); setEditItem(null); }} style={{
            background: VERT, color: '#fff', border: 'none', borderRadius: 6,
            padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
          }}>+ Ajouter</button>
        </div>

        {/* Formulaire d'ajout */}
        {showForm && (
          <div style={{
            background: '#F8F9FA', borderRadius: 8, padding: '14px 16px',
            marginBottom: 16, border: `1px solid ${GRIS_BORD}`,
          }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <input placeholder="Code (ex: DEMISSION)" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                style={{ ...inp, width: 160 }} />
              <input placeholder="Label affiché" value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                style={{ ...inp, flex: 1, minWidth: 150 }} />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <ColorPicker selected={form.couleur} onChange={c => setForm({ ...form, couleur: c })} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <Toggle on={form.actif} onClick={() => setForm({ ...form, actif: !form.actif })} />
                Actif
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <BtnSave onClick={ajouter} />
                <BtnCancel onClick={() => setShowForm(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Liste des motifs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {motifs.map(m => {
            const enEdition = editItem?.id === m.id;
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 8,
                background: enEdition ? '#F1F8E9' : '#F8F9FA',
                border: `1px solid ${enEdition ? '#AED581' : GRIS_BORD}`,
              }}>
                {enEdition ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input value={editItem.label}
                        onChange={e => setEditItem({ ...editItem, label: e.target.value })}
                        style={{ ...inp, flex: 1, minWidth: 150 }} />
                      <ColorPicker selected={editItem.couleur} onChange={c => setEditItem({ ...editItem, couleur: c })} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                        <Toggle on={editItem.actif} onClick={() => setEditItem({ ...editItem, actif: !editItem.actif })} />
                        Actif
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                      <BtnSave onClick={sauvegarder} />
                      <BtnCancel onClick={() => setEditItem(null)} />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: m.couleur, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{m.label}</span>
                      <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{m.code}</span>
                      {m.actif
                        ? <span style={{ background: '#E8F5E9', color: VERT,   fontSize: 11, padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>Actif</span>
                        : <span style={{ background: '#F5F5F5', color: '#888', fontSize: 11, padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>Inactif</span>
                      }
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <BtnEdit onClick={() => { setEditItem({ ...m }); setShowForm(false); }} />
                      <BtnDel onClick={() => supprimer(m.id)} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
};

// ── ProvisionsSection — défini HORS de ParametresPage ──────────────────────

const COULEURS_TRANCHES = ['#1B5E20', '#388E3C', '#81C784', '#C62828', '#EF9F27', '#378ADD'];

const ProvisionsSection = () => {
  const [tranches, setTranches]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [localMsg, setLocalMsg]   = useState(null);
  const [simAnciennete]           = useState(23);
  const SMM                       = 250000;

  const toArray = (data) => Array.isArray(data) ? data : (data?.rows || data?.data || []);

  const charger = async () => {
    try {
      const res = await axios.get('/api/parametres/tranches');
      setTranches(toArray(res.data?.data).sort((a, b) => a.ordre - b.ordre));
    } catch { setLocalMsg({ type: 'error', text: 'Erreur chargement tranches' }); }
  };

  useEffect(() => { charger(); }, []);

  const afficherMsg = (type, text) => {
    setLocalMsg({ type, text });
    setTimeout(() => setLocalMsg(null), 3000);
  };

  const updateLocal = (idx, champ, valeur) => {
    setTranches(prev => {
      const next = prev.map((t, i) => i === idx ? { ...t, [champ]: valeur } : t);
      if (champ === 'a_annee') {
        const nv = valeur === '' ? null : Number(valeur);
        if (idx + 1 < next.length) {
          next[idx + 1] = { ...next[idx + 1], de_annee: nv ?? next[idx + 1].de_annee };
        }
      }
      return next;
    });
  };

  const sauvegarderTranche = async (t) => {
    setSaving(true);
    try {
      await axios.put(`/api/parametres/tranches/${t.id}`, {
        de_annee: Number(t.de_annee),
        a_annee: t.a_annee !== null && t.a_annee !== '' ? Number(t.a_annee) : null,
        taux: Number(t.taux),
        ordre: Number(t.ordre),
      });
      afficherMsg('ok', 'Tranche sauvegardée');
    } catch (e) {
      afficherMsg('error', e.response?.data?.message || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const ajouterTranche = async () => {
    const derniere = tranches[tranches.length - 1];
    const nouvelleBase = derniere?.a_annee ?? (Number(derniere?.de_annee || 0) + 5);

    const updatedTranches = tranches.map((t, i) =>
      i === tranches.length - 1 ? { ...t, a_annee: nouvelleBase } : t
    );

    setSaving(true);
    try {
      if (derniere) {
        await axios.put(`/api/parametres/tranches/${derniere.id}`, {
          de_annee: Number(derniere.de_annee),
          a_annee: Number(nouvelleBase),
          taux: Number(derniere.taux),
          ordre: Number(derniere.ordre),
        });
      }
      const res = await axios.post('/api/parametres/tranches', {
        de_annee: Number(nouvelleBase),
        a_annee: null,
        taux: 50,
        ordre: tranches.length + 1,
      });
      setTranches([...updatedTranches, res.data?.data ?? { id: Date.now(), de_annee: nouvelleBase, a_annee: null, taux: 50, ordre: tranches.length + 1 }]);
      afficherMsg('ok', 'Tranche ajoutée');
    } catch (e) {
      afficherMsg('error', e.response?.data?.message || 'Erreur ajout');
    } finally { setSaving(false); }
  };

  const supprimerTranche = async (t) => {
    if (tranches.length <= 1) return;
    setSaving(true);
    try {
      await axios.delete(`/api/parametres/tranches/${t.id}`);
      await charger();
      afficherMsg('ok', 'Tranche supprimée');
    } catch (e) {
      afficherMsg('error', e.response?.data?.message || 'Erreur suppression');
    } finally { setSaving(false); }
  };

  const calculerSimulation = () => {
    let total = 0;
    const details = [];
    for (const t of tranches) {
      const fin = t.a_annee !== null && t.a_annee !== '' ? Number(t.a_annee) : Infinity;
      const debut = Number(t.de_annee);
      if (simAnciennete <= debut) continue;
      const anneesDansTranche = Math.min(simAnciennete, fin) - debut;
      if (anneesDansTranche <= 0) continue;
      const montant = SMM * (Number(t.taux) / 100) * anneesDansTranche;
      total += montant;
      details.push({ label: `${debut}→${fin === Infinity ? '∞' : fin} ans`, taux: t.taux, annees: anneesDansTranche, montant });
    }
    return { total, details };
  };

  const { total, details } = calculerSimulation();

  const styleEntete = { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 10px', background: '#F8F9FA', borderBottom: `1px solid ${GRIS_BORD}` };
  const styleCellule = { padding: '10px 10px', borderBottom: `1px solid #F5F5F5`, verticalAlign: 'middle' };
  const styleInpNum = { ...inp, width: 65, textAlign: 'center', fontSize: 14, fontWeight: 600 };

  return (
    <>
      {localMsg && (
        <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13, background: localMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: localMsg.type === 'ok' ? VERT : ROUGE, border: `1px solid ${localMsg.type === 'ok' ? '#A5D6A7' : '#FFCDD2'}` }}>
          {localMsg.text}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>Provisions retraite</h2>
          <p style={{ margin: 0, fontSize: 12, color: '#888' }}>Barème des indemnités de fin de carrière — Code du Travail sénégalais</p>
        </div>
        <button
          onClick={ajouterTranche}
          disabled={saving}
          style={{ padding: '8px 16px', background: VERT, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
        >
          + Ajouter une tranche
        </button>
      </div>

      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={styleEntete}>DE (ans)</th>
              <th style={styleEntete}>À (ans)</th>
              <th style={styleEntete}>TAUX (%)</th>
              <th style={styleEntete}>APERÇU</th>
              <th style={{ ...styleEntete, textAlign: 'center' }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {tranches.map((t, idx) => {
              const couleur = COULEURS_TRANCHES[idx] || '#888780';
              const estPremiere = idx === 0;
              const estDerniere = idx === tranches.length - 1;
              const labelA = estDerniere ? '∞' : (t.a_annee ?? '∞');
              return (
                <tr key={t.id}>
                  <td style={styleCellule}>
                    <input
                      type="number" min={0}
                      value={t.de_annee}
                      disabled={estPremiere}
                      onChange={e => updateLocal(idx, 'de_annee', e.target.value)}
                      onBlur={() => sauvegarderTranche(t)}
                      style={{ ...styleInpNum, background: estPremiere ? '#F5F5F5' : '#fff', cursor: estPremiere ? 'default' : 'text' }}
                    />
                  </td>
                  <td style={styleCellule}>
                    {estDerniere
                      ? <input type="text" value="∞" disabled style={{ ...styleInpNum, background: '#F5F5F5', cursor: 'default' }} />
                      : <input
                          type="number" min={Number(t.de_annee) + 1}
                          value={t.a_annee ?? ''}
                          onChange={e => updateLocal(idx, 'a_annee', e.target.value)}
                          onBlur={() => sauvegarderTranche(t)}
                          style={styleInpNum}
                        />
                    }
                  </td>
                  <td style={styleCellule}>
                    <input
                      type="number" min={0} max={100} step={0.5}
                      value={t.taux}
                      onChange={e => updateLocal(idx, 'taux', e.target.value)}
                      onBlur={() => sauvegarderTranche(t)}
                      style={styleInpNum}
                    />
                  </td>
                  <td style={styleCellule}>
                    <div style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 12, background: couleur + '20', border: `1px solid ${couleur}40`, fontSize: 11, fontWeight: 600, color: couleur, whiteSpace: 'nowrap' }}>
                      SMM × {t.taux}% × {t.de_annee}→{labelA} ans
                    </div>
                  </td>
                  <td style={{ ...styleCellule, textAlign: 'center' }}>
                    <button
                      onClick={() => supprimerTranche(t)}
                      disabled={tranches.length <= 1 || saving}
                      title={tranches.length <= 1 ? 'Impossible de supprimer la dernière tranche' : 'Supprimer'}
                      style={{ padding: '4px 8px', background: tranches.length <= 1 ? '#F5F5F5' : '#FFEBEE', color: tranches.length <= 1 ? '#CCC' : ROUGE, border: `1px solid ${tranches.length <= 1 ? '#EEE' : '#FFCDD2'}`, borderRadius: 4, cursor: tranches.length <= 1 || saving ? 'not-allowed' : 'pointer', fontSize: 13 }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Simulation */}
      <div style={{ marginTop: 20, padding: 16, background: '#F8F9FA', borderRadius: 8, border: `1px solid ${GRIS_BORD}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 12 }}>
          Simulation — {simAnciennete} ans d'ancienneté, SMM = {SMM.toLocaleString('fr-FR')} FCFA
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {details.map((d, i) => (
            <div key={i} style={{ padding: '6px 12px', background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 12 }}>
              <span style={{ color: '#888' }}>{d.label} ({d.annees} an{d.annees > 1 ? 's' : ''})</span>
              <span style={{ marginLeft: 8, fontWeight: 700, color: '#222' }}>{Math.round(d.montant).toLocaleString('fr-FR')} FCFA</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: VERT }}>
          Total : {Math.round(total).toLocaleString('fr-FR')} FCFA
        </div>
      </div>
    </>
  );
};

// ── NotificationsSection — défini HORS de ParametresPage ───────────────────

const NotificationsSection = () => {
  const [notifs, setNotifs] = useState({ destinataires: [], evenements: [] });
  const [saving, setSaving] = useState(null);
  const [localMsg, setLocalMsg] = useState(null);

  const charger = async () => {
    try {
      const res = await axios.get('/api/parametres/notifs');
      setNotifs({
        destinataires: res.data?.data?.destinataires || [],
        evenements:    res.data?.data?.evenements    || [],
      });
    } catch { setLocalMsg({ type: 'error', text: 'Erreur chargement notifications' }); }
  };

  useEffect(() => { charger(); }, []);

  const toggle = async (cle, valeurActuelle) => {
    setSaving(cle);
    try {
      await axios.put(`/api/parametres/notifs/${cle}`, { valeur: !valeurActuelle });
      setNotifs(prev => {
        const update = (arr) => arr.map(n => n.cle === cle ? { ...n, valeur: !valeurActuelle } : n);
        return { destinataires: update(prev.destinataires), evenements: update(prev.evenements) };
      });
      setLocalMsg({ type: 'ok', text: 'Sauvegardé' });
      setTimeout(() => setLocalMsg(null), 2000);
    } catch (e) {
      setLocalMsg({ type: 'error', text: e.response?.data?.message || 'Erreur sauvegarde' });
    } finally { setSaving(null); }
  };

  const styleToggle = (on) => ({
    position: 'relative', display: 'inline-block',
    width: 36, height: 20, borderRadius: 20, cursor: 'pointer',
    background: on ? VERT : '#CCC',
    transition: 'background 0.2s',
    flexShrink: 0,
  });
  const styleKnob = (on) => ({
    position: 'absolute', top: 3, left: on ? 19 : 3,
    width: 14, height: 14, borderRadius: '50%',
    background: '#fff', transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  });
  const styleRow = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 0', borderBottom: `1px solid #F5F5F5`,
  };
  const styleBlocTitre = {
    fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase',
    letterSpacing: '0.07em', marginBottom: 4, paddingBottom: 10,
    borderBottom: `1px solid ${GRIS_BORD}`,
  };

  const renderBloc = (titre, items) => (
    <Card>
      <div style={styleBlocTitre}>{titre}</div>
      {items.length === 0 && <div style={{ fontSize: 13, color: '#AAA', padding: '8px 0' }}>Aucun paramètre</div>}
      {items.map((n, idx) => (
        <div key={n.cle} style={{ ...styleRow, borderBottom: idx === items.length - 1 ? 'none' : `1px solid #F5F5F5` }}>
          <span style={{ fontSize: 14, color: '#333' }}>{n.label}</span>
          <div
            style={{ ...styleToggle(n.valeur), opacity: saving === n.cle ? 0.6 : 1 }}
            onClick={() => saving === null && toggle(n.cle, n.valeur)}
            title={n.valeur ? 'Activé — cliquer pour désactiver' : 'Désactivé — cliquer pour activer'}
          >
            <div style={styleKnob(n.valeur)} />
          </div>
        </div>
      ))}
    </Card>
  );

  return (
    <>
      {localMsg && (
        <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13, background: localMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: localMsg.type === 'ok' ? VERT : ROUGE, border: `1px solid ${localMsg.type === 'ok' ? '#A5D6A7' : '#FFCDD2'}` }}>
          {localMsg.text}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>Notifications</h2>
        <p style={{ margin: 0, fontSize: 12, color: '#888' }}>Configurez qui reçoit les alertes et quels événements les déclenchent</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {renderBloc('Destinataires des alertes retraite', notifs.destinataires)}
        {renderBloc('Événements déclencheurs', notifs.evenements)}
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#F8F9FA', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, fontSize: 12, color: '#777' }}>
        Les notifications sont envoyées via le système in-app (cloche).
      </div>
    </>
  );
};

// ── AgesSection — défini HORS de ParametresPage ────────────────────────────

const CLES_AGES = [
  { cle: 'age_retraite_hommes', label: 'Âge retraite hommes', unite: 'ans' },
  { cle: 'age_retraite_femmes', label: 'Âge retraite femmes', unite: 'ans' },
  { cle: 'age_retraite_max',    label: 'Âge maximum',         unite: 'ans' },
  { cle: 'date_reference_jour', label: 'Jour de référence',   unite: '' },
  { cle: 'date_reference_mois', label: 'Mois de référence',   unite: '' },
];

const CLES_ALERTES = [
  { cle: 'alerte_critique_mois', label: 'Alerte critique',  couleur: '#E24B4A' },
  { cle: 'alerte_urgent_mois',   label: 'Alerte urgente',   couleur: '#EF9F27' },
  { cle: 'alerte_precoce_mois',  label: 'Alerte précoce',   couleur: '#378ADD' },
  { cle: 'alerte_info_mois',     label: 'Alerte info',      couleur: '#1B5E20' },
];

const AgesSection = () => {
  const [params, setParams]     = useState({});
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState({});
  const [localMsg, setLocalMsg] = useState(null);

  const charger = async () => {
    try {
      const res = await axios.get('/api/parametres/retraite');
      const map = {};
      (Array.isArray(res.data?.data) ? res.data.data : []).forEach(r => { map[r.cle] = r.valeur; });
      setParams(map);
      setForm(map);
    } catch { setLocalMsg({ type: 'error', text: 'Erreur chargement' }); }
  };

  useEffect(() => { charger(); }, []);

  const sauvegarder = async () => {
    try {
      await axios.put('/api/parametres/retraite', { valeurs: form });
      setParams({ ...form });
      setEditing(false);
      setLocalMsg({ type: 'ok', text: 'Paramètres sauvegardés' });
      setTimeout(() => setLocalMsg(null), 3000);
    } catch (e) {
      setLocalMsg({ type: 'error', text: e.response?.data?.message || 'Erreur sauvegarde' });
    }
  };

  const styleBlocTitre = {
    fontSize: 13, fontWeight: 700, color: '#555', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 12, paddingBottom: 8,
    borderBottom: `1px solid ${GRIS_BORD}`,
  };
  const styleRow = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', borderBottom: `1px solid #F5F5F5`,
  };
  const styleLabel = { fontSize: 13, color: '#444', fontWeight: 500 };
  const styleValeur = { fontSize: 15, fontWeight: 700, color: '#222' };
  const styleInpSmall = { ...inp, width: 70, textAlign: 'center', fontSize: 14, fontWeight: 700 };

  return (
    <>
      {localMsg && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13,
          background: localMsg.type === 'ok' ? '#E8F5E9' : '#FFEBEE',
          color: localMsg.type === 'ok' ? VERT : ROUGE,
          border: `1px solid ${localMsg.type === 'ok' ? '#A5D6A7' : '#FFCDD2'}`,
        }}>{localMsg.text}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>Ages & alertes retraite</h2>
        {!editing
          ? <button onClick={() => setEditing(true)} style={{ padding: '8px 16px', background: VERT, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Modifier</button>
          : <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={sauvegarder} style={{ padding: '8px 16px', background: VERT, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Sauvegarder</button>
              <button onClick={() => { setForm(params); setEditing(false); }} style={{ padding: '8px 16px', background: '#f5f5f5', color: '#444', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            </div>
        }
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Bloc Ages de référence */}
        <Card>
          <div style={styleBlocTitre}>Ages de référence</div>
          {CLES_AGES.map(({ cle, label, unite }) => (
            <div key={cle} style={styleRow}>
              <span style={styleLabel}>{label}</span>
              {editing
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" value={form[cle] ?? ''} min={0} max={100}
                      onChange={e => setForm(f => ({ ...f, [cle]: e.target.value }))}
                      style={styleInpSmall} />
                    {unite && <span style={{ fontSize: 12, color: '#888' }}>{unite}</span>}
                  </div>
                : <span style={styleValeur}>{params[cle] ?? '—'}{unite ? <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 3 }}>{unite}</span> : null}</span>
              }
            </div>
          ))}
        </Card>

        {/* Bloc Seuils d'alerte */}
        <Card>
          <div style={styleBlocTitre}>Seuils d'alerte (avant retraite)</div>
          {CLES_ALERTES.map(({ cle, label, couleur }) => (
            <div key={cle} style={styleRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: couleur, flexShrink: 0 }} />
                <span style={styleLabel}>{label}</span>
              </div>
              {editing
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" value={form[cle] ?? ''} min={1} max={120}
                      onChange={e => setForm(f => ({ ...f, [cle]: e.target.value }))}
                      style={styleInpSmall} />
                    <span style={{ fontSize: 12, color: '#888' }}>mois</span>
                  </div>
                : <span style={styleValeur}>{params[cle] ?? '—'}<span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 3 }}>mois</span></span>
              }
            </div>
          ))}
          {!editing && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#F8F9FA', borderRadius: 6, fontSize: 12, color: '#666', lineHeight: 1.5 }}>
              Un employé est alerté selon le délai restant avant son âge de retraite légal.
            </div>
          )}
        </Card>

      </div>
    </>
  );
};

// ── ImportExportSection — défini HORS de ParametresPage ─────────────────────

const API_IMPORT = process.env.REACT_APP_API_URL || '';

const BADGE_IMPORT = {
  cree:       { bg: '#E8F5E9', color: '#2E7D32', label: 'Créé' },
  mis_a_jour: { bg: '#E3F2FD', color: '#1565C0', label: 'Mis à jour' },
  erreur:     { bg: '#FFEBEE', color: '#C62828',  label: 'Erreur' },
};

const ImportStatCard = ({ couleur, label, valeur, icone }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', borderLeft: `4px solid ${couleur}`, display: 'flex', alignItems: 'center', gap: 12 }}>
    {icone}
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: couleur }}>{valeur}</div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const ImportExportSection = () => {
  const [etape,      setEtape]      = useState('attente');
  const [fichier,    setFichier]    = useState(null);
  const [survol,     setSurvol]     = useState(false);
  const [rapport,    setRapport]    = useState(null);
  const [accesInitiaux, setAccesInitiaux] = useState([]);
  const [erreurPage, setErreurPage] = useState('');
  const [historique, setHistorique] = useState([]);
  const [showHisto,  setShowHisto]  = useState(false);
  const [loadHisto,  setLoadHisto]  = useState(false);
  const [expandErr,  setExpandErr]  = useState(false);

  const inputRef = useRef(null);
  const token    = localStorage.getItem('sogas_token');
  const headers  = { Authorization: `Bearer ${token}` };

  const selectionnerFichier = (f) => {
    if (!f.name.endsWith('.xlsx')) { setErreurPage('Seuls les fichiers .xlsx sont acceptés.'); return; }
    setFichier(f); setErreurPage(''); setRapport(null); setAccesInitiaux([]); setEtape('attente');
  };

  const onDrop = (e) => {
    e.preventDefault(); setSurvol(false);
    const f = e.dataTransfer.files[0];
    if (f) selectionnerFichier(f);
  };

  const telechargerTemplate = async () => {
    try {
      const res = await axios.get(`${API_IMPORT}/api/import/template`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = 'template_import_employes.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setErreurPage('Impossible de télécharger le template.'); }
  };

  const lancerImport = async () => {
    if (!fichier) return;
    setEtape('chargement'); setErreurPage('');
    const form = new FormData();
    form.append('fichier', fichier);
    try {
      const res = await axios.post(`${API_IMPORT}/api/import/employes`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setRapport(res.data.rapport);
      // Mots de passe provisoires : uniquement dans cette réponse, affichés une seule fois.
      setAccesInitiaux(Array.isArray(res.data.acces_initiaux) ? res.data.acces_initiaux : []); setEtape('resultat');
    } catch (err) {
      setErreurPage(err.response?.data?.message || "Erreur lors de l'import.");
      setEtape('attente');
    }
  };

  const chargerHistorique = async () => {
    setLoadHisto(true);
    try {
      const res = await axios.get(`${API_IMPORT}/api/import/historique`, { headers });
      setHistorique(res.data); setShowHisto(true);
    } catch { setErreurPage('Erreur chargement historique.'); }
    finally { setLoadHisto(false); }
  };

  const reset = () => { setFichier(null); setRapport(null); setAccesInitiaux([]); setEtape('attente'); setErreurPage(''); setExpandErr(false); };

  const lignesErreur = rapport ? rapport.lignes.filter(l => l.action === 'erreur')     : [];
  const lignesCrees  = rapport ? rapport.lignes.filter(l => l.action === 'cree')       : [];

  const styleBtnOutline = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', border: `1.5px solid ${VERT}`, borderRadius: 8, background: '#fff', color: VERT, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
  const styleTd = { padding: '7px 10px', borderBottom: '1px solid #F0F0F0', verticalAlign: 'top', fontSize: 12 };
  const styleTdh = { padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: '1px solid #E5E5E5', whiteSpace: 'nowrap', fontSize: 12 };

  return (
    <>
      {/* En-tête section */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>Import / Export</h2>
          <p style={{ margin: 0, fontSize: 12, color: '#888' }}>Importez plusieurs employés via un fichier Excel .xlsx</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={telechargerTemplate} style={styleBtnOutline}>
            <Download size={13} /> Template
          </button>
          <button onClick={loadHisto ? undefined : chargerHistorique} style={{ ...styleBtnOutline, opacity: loadHisto ? 0.6 : 1 }}>
            {loadHisto ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Clock size={13} />}
            Historique
          </button>
        </div>
      </div>

      {erreurPage && (
        <div style={{ background: '#FFF0F0', border: '1px solid #EF9A9A', borderRadius: 8, padding: '10px 14px', color: ROUGE, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <XCircle size={14} /> {erreurPage}
        </div>
      )}

      {/* Zone dépôt */}
      {etape !== 'resultat' && (
        <Card style={{ marginBottom: 16 }}>
          <div
            style={{ border: `2px dashed ${survol ? VERT : (fichier ? '#2E7D32' : '#ccc')}`, borderRadius: 12, padding: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', background: survol ? '#F1F8E9' : (fichier ? '#F9FFF9' : '#FAFAFA') }}
            onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={e => e.target.files[0] && selectionnerFichier(e.target.files[0])} />
            {fichier ? (
              <div style={{ textAlign: 'center' }}>
                <FileSpreadsheet size={34} color={VERT} style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 700, color: VERT, fontSize: 14 }}>{fichier.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{(fichier.size / 1024).toFixed(1)} Ko · Cliquer pour changer</div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                <Upload size={34} color="#bbb" style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Glisser-déposer votre fichier .xlsx ici</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>ou cliquer pour parcourir · max 5 Mo</div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Bouton import */}
      {fichier && etape !== 'resultat' && (
        <button onClick={lancerImport} disabled={etape === 'chargement'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', height: 46, background: VERT, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 16, opacity: etape === 'chargement' ? 0.7 : 1, fontFamily: 'inherit' }}>
          {etape === 'chargement'
            ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> Import en cours…</>
            : <><Upload size={15} /> Lancer l'import</>}
        </button>
      )}

      {/* Résultats */}
      {etape === 'resultat' && rapport && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
            <ImportStatCard couleur="#1565C0" label="Lignes traitées"  valeur={rapport.nb_lignes}     icone={<FileSpreadsheet size={18} color="#1565C0" />} />
            <ImportStatCard couleur={VERT}    label="Employés créés"   valeur={rapport.nb_crees}      icone={<CheckCircle size={18} color={VERT} />} />
            <ImportStatCard couleur="#0277BD" label="Mis à jour"       valeur={rapport.nb_mis_a_jour} icone={<RefreshCw size={18} color="#0277BD" />} />
            <ImportStatCard couleur={ROUGE}   label="Erreurs"          valeur={rapport.nb_erreurs}    icone={<XCircle size={18} color={ROUGE} />} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16, background: rapport.nb_erreurs === 0 ? '#E8F5E9' : '#FFF8E1', borderColor: rapport.nb_erreurs === 0 ? '#A5D6A7' : '#FFD54F', color: rapport.nb_erreurs === 0 ? '#2E7D32' : '#E65100' }}>
            {rapport.nb_erreurs === 0
              ? <><CheckCircle size={16} /> Import terminé sans erreur — {rapport.nb_crees} créé(s), {rapport.nb_mis_a_jour} mis à jour</>
              : <><AlertCircle size={16} /> Import terminé avec {rapport.nb_erreurs} erreur(s) sur {rapport.nb_lignes} lignes</>}
          </div>

          {accesInitiaux.length > 0 && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 8 }}>
                <CheckCircle size={14} color={VERT} /> Mots de passe provisoires générés
              </div>
              <p style={{ fontSize: 11, color: '#888', margin: '0 0 10px 0' }}>Affichés une seule fois : ils ne sont conservés nulle part, ni dans l'historique des imports. Remettez-les aux salariés de façon confidentielle ; ils devront changer leur mot de passe à la première connexion. Les comptes importés ont tous le rôle Employé (les rôles du fichier ne sont pas importés).</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ background: '#F5F5F5' }}>
                    {['Ligne','Matricule','Mot de passe temporaire'].map(h => <th key={h} style={styleTdh}>{h}</th>)}
                  </tr></thead>
                  <tbody>{accesInitiaux.map((l, i) => (
                    <tr key={i} style={{ background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                      <td style={styleTd}>{l.ligne}</td>
                      <td style={{ ...styleTd, fontWeight: 700 }}>{l.matricule}</td>
                      <td style={{ ...styleTd, fontFamily: 'monospace', color: VERT, fontWeight: 700 }}>{l.mot_de_passe_provisoire}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </Card>
          )}

          {lignesErreur.length > 0 && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: ROUGE, cursor: 'pointer', marginBottom: 2 }} onClick={() => setExpandErr(v => !v)}>
                <XCircle size={14} color={ROUGE} /> {lignesErreur.length} ligne(s) en erreur
                <span style={{ marginLeft: 'auto' }}>{expandErr ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
              </div>
              {expandErr && (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ background: '#F5F5F5' }}>
                      {['Ligne','Matricule','Erreur(s)'].map(h => <th key={h} style={styleTdh}>{h}</th>)}
                    </tr></thead>
                    <tbody>{lignesErreur.map((l, i) => (
                      <tr key={i} style={{ background: '#FFF8F8' }}>
                        <td style={styleTd}>{l.ligne}</td>
                        <td style={{ ...styleTd, fontWeight: 600 }}>{l.matricule || '—'}</td>
                        <td style={{ ...styleTd, color: ROUGE }}>{l.erreurs.join(' · ')}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          <button onClick={reset} style={{ ...styleBtnOutline, marginTop: 8 }}>
            <RefreshCw size={13} /> Nouvel import
          </button>
        </div>
      )}

      {/* Historique */}
      {showHisto && (
        <Card style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
              <Clock size={14} color={VERT} /> Historique des imports
            </div>
            <button onClick={() => setShowHisto(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 20, lineHeight: 1 }}>×</button>
          </div>
          {historique.length === 0 ? (
            <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: 16 }}>Aucun import</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: '#F5F5F5' }}>
                  {['Date','Fichier','Par','Lignes','Créés','Mis à jour','Erreurs'].map(h => <th key={h} style={styleTdh}>{h}</th>)}
                </tr></thead>
                <tbody>{historique.map((h, i) => (
                  <tr key={i} style={{ background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                    <td style={styleTd}>{new Date(h.created_at).toLocaleString('fr-FR')}</td>
                    <td style={{ ...styleTd, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.nom_fichier}</td>
                    <td style={styleTd}>{h.importe_par_nom || '—'}</td>
                    <td style={{ ...styleTd, textAlign: 'center' }}>{h.nb_lignes}</td>
                    <td style={{ ...styleTd, textAlign: 'center', color: VERT, fontWeight: 700 }}>{h.nb_crees}</td>
                    <td style={{ ...styleTd, textAlign: 'center', color: '#1565C0', fontWeight: 700 }}>{h.nb_mis_a_jour}</td>
                    <td style={{ ...styleTd, textAlign: 'center', color: h.nb_erreurs > 0 ? ROUGE : '#999', fontWeight: h.nb_erreurs > 0 ? 700 : 400 }}>{h.nb_erreurs}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Info bas si vide */}
      {etape === 'attente' && !fichier && (
        <div style={{ background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16 }}>
          <AlertCircle size={14} color="#1565C0" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#1565C0', lineHeight: 1.6 }}>
            <strong>Comment ça marche ?</strong><br />
            1. Téléchargez le template Excel ci-dessus<br />
            2. Remplissez les données des employés à partir de la ligne 4 (lignes 1–3 = en-tête + exemple)<br />
            3. Les employés existants (même matricule) seront mis à jour sans changer leur mot de passe<br />
            4. Les nouveaux employés recevront un mot de passe temporaire (première connexion obligatoire)
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

// ── PretsSogasSection ──────────────────────────────────────────────────────

const PRETS_PARAMS = [
  { cle: 'plafond_montant_cdi',    label: 'Plafond CDI',               unite: 'FCFA', desc: 'Montant maximum pour un CDI' },
  { cle: 'plafond_montant_cdd',    label: 'Plafond CDD',               unite: 'FCFA', desc: 'Montant maximum pour un CDD' },
  { cle: 'duree_min_mois',         label: 'Durée minimale',            unite: 'mois', desc: 'Nombre de mois minimum de remboursement' },
  { cle: 'duree_max_mois',         label: 'Durée maximale',            unite: 'mois', desc: 'Nombre de mois maximum de remboursement' },
  { cle: 'anciennete_min_mois',    label: 'Ancienneté minimale',       unite: 'mois', desc: 'Ancienneté requise avant de pouvoir demander un prêt' },
  { cle: 'seuil_solvabilite_pct',  label: 'Seuil solvabilité',         unite: '%',    desc: "Part maximale du salaire consacrée aux retenues" },
  { cle: 'delai_entre_prets_mois', label: 'Délai entre prêts',         unite: 'mois', desc: 'Délai minimum après solde du dernier prêt' },
];

const PretsSogasSection = () => {
  const { token } = useAuth();
  const [params, setParams]   = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving]   = useState({});
  const [msg, setMsg]         = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const loadParams = () => {
    if (!token) return;
    axios.get(`${API}/prets-sogas/parametres`, h)
      .then(r => {
        const map = {};
        (r.data.parametres || []).forEach(p => { map[p.cle] = p; });
        setParams(map);
      })
      .catch(console.error);
  };

  useEffect(() => { loadParams(); }, [token]); // eslint-disable-line

  const save = async (cle) => {
    setSaving(prev => ({ ...prev, [cle]: true }));
    try {
      await axios.put(`${API}/prets-sogas/parametres/${cle}`, { valeur: editing[cle] }, h);
      setParams(prev => ({ ...prev, [cle]: { ...prev[cle], valeur: editing[cle] } }));
      setEditing(prev => { const n = { ...prev }; delete n[cle]; return n; });
      setMsg({ type: 'ok', text: 'Paramètre mis à jour' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.message || 'Erreur' });
    } finally { setSaving(prev => ({ ...prev, [cle]: false })); }
  };

  const CARD_S = { background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, marginBottom: 16 };
  const HEAD_S = { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: `1px solid ${GRIS_BORD}`, fontWeight: 700, fontSize: 14, color: VERT };
  const ROW_S  = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: `1px solid #F5F5F5`, gap: 12 };
  const LBL_S  = { flex: 1, minWidth: 0 };
  const VAL_S  = { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 };

  return (
    <div>
      {msg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: msg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: msg.type === 'ok' ? VERT : ROUGE, fontWeight: 600, fontSize: 13 }}>
          {msg.text}
        </div>
      )}
      <div style={CARD_S}>
        <div style={HEAD_S}>
          <CreditCard size={16} />
          Paramètres du module Prêts SOGAS
        </div>
        {PRETS_PARAMS.map((p, i) => {
          const param = params[p.cle];
          const valeur = param?.valeur ?? '—';
          const isEditing = p.cle in editing;
          const isSaving = saving[p.cle];
          return (
            <div key={p.cle} style={{ ...ROW_S, borderBottom: i < PRETS_PARAMS.length - 1 ? `1px solid #F5F5F5` : 'none' }}>
              <div style={LBL_S}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>{p.label}</div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>{p.desc}</div>
              </div>
              <div style={VAL_S}>
                {isEditing ? (
                  <>
                    <input
                      type="number"
                      value={editing[p.cle]}
                      onChange={e => setEditing(prev => ({ ...prev, [p.cle]: e.target.value }))}
                      style={{ ...inp, width: 100, textAlign: 'right' }}
                      autoFocus
                    />
                    <span style={{ fontSize: 12, color: '#64748B', minWidth: 32 }}>{p.unite}</span>
                    <button
                      onClick={() => save(p.cle)}
                      disabled={isSaving}
                      style={{ padding: '5px 12px', background: VERT, color: '#fff', border: 0, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    >{isSaving ? '...' : 'Enregistrer'}</button>
                    <button
                      onClick={() => setEditing(prev => { const n = { ...prev }; delete n[p.cle]; return n; })}
                      style={{ padding: '5px 10px', background: 'transparent', color: '#64748B', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
                    >Annuler</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1E293B', fontVariantNumeric: 'tabular-nums' }}>
                      {p.unite === 'FCFA' ? Number(valeur).toLocaleString('fr-FR') : valeur}
                    </span>
                    <span style={{ fontSize: 12, color: '#64748B', minWidth: 32 }}>{p.unite}</span>
                    <button
                      onClick={() => setEditing(prev => ({ ...prev, [p.cle]: valeur }))}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'transparent', color: VERT, border: `1px solid #C8E6C9`, borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
                    ><Pencil size={12} /> Modifier</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// RH-PAIE-2 — Paramètres de paie versionnés. Suit le même pattern inline que IpmSection/
// PretsSogasSection ci-dessous (déjà établi dans ce fichier).
const COULEUR_STATUT_PAIE = {
  BROUILLON: '#888', A_VALIDER: '#E65100', VALIDE: VERT, PROGRAMME: '#E65100',
  ACTIF: VERT, ARCHIVE: '#888', ANNULE: ROUGE,
};

function BadgePaie({ texte, couleur }) {
  return <span style={{ background: couleur + '1a', color: couleur, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{texte}</span>;
}

// RH-PAIE-2D §24 — badge de statut humanisé, réutilisé partout (paramètres, rubriques, grille).
// Un niveau_confiance INTERDIT_DE_CALCUL prime toujours sur le statut brut (jamais présenté
// comme un simple "Validé"/"Actif" si la donnée est marquée non utilisable pour le calcul).
function BadgeStatutPaie({ statut, niveauConfiance }) {
  if (niveauConfiance === 'INTERDIT_DE_CALCUL') {
    return <BadgePaie texte={humaniserNiveauConfiance(niveauConfiance)} couleur={COULEUR_INTERDIT_DE_CALCUL} />;
  }
  return <BadgePaie texte={humaniserStatut(statut)} couleur={COULEUR_STATUT_PAIE[statut] || '#888'} />;
}

function ModalPaie({ titre, onClose, children, large }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: large ? 640 : 440, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, color: VERT, fontSize: 15 }}>{titre}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// RH-PAIE-2H — message d'échec de validation compréhensible, jamais un nom technique de
// permission exposé à l'écran. La validation des paramètres/grille/rubriques de paie est
// réservée au DRH dans tout le module (voir GUIDE-RECETTE-VISUELLE-RH-PAIE-1-2.md §5) : un 403
// sur cette action précise a donc toujours la même explication fonctionnelle.
function messageErreurValidation(e) {
  const backendMsg = e.response?.data?.message;
  if (e.response?.status === 403) {
    return `${backendMsg || 'Action non autorisée.'} La validation est réservée au rôle DRH — demandez à un compte DRH de valider cette ligne, ou contactez la DRH si vous pensez que votre compte devrait y avoir accès.`;
  }
  return backendMsg || 'Erreur lors de la validation. Réessayez ou contactez le support si le problème persiste.';
}

// RH-PAIE-2D §9-10 — Modale de validation sécurisée, réutilisée pour paramètres et rubriques.
// Jamais un simple clic : synthèse complète + commentaire obligatoire + confirmation explicite.
// RH-PAIE-2H — l'erreur d'une tentative de validation échouée (ex. permission insuffisante)
// s'affiche désormais À L'INTÉRIEUR de cette modale, jamais dans la bannière globale derrière
// elle : la modale reste ouverte, le commentaire et la case cochée restent intacts (état local
// du composant, jamais réinitialisé par un échec), le bouton ne reste jamais bloqué en
// "Validation…" (géré par le `finally` de chaque appelant).
function ModaleValidationSecurisee({ titre, synthese, avertissement, erreur, onClose, onConfirmer, saving }) {
  const [commentaire, setCommentaire] = useState('');
  const [confirme, setConfirme] = useState(false);
  const peutValider = commentaire.trim().length > 0 && confirme;
  return (
    <ModalPaie titre={titre} onClose={onClose} large>
      {erreur && (
        <div style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', color: ROUGE, borderRadius: 8, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
          {erreur}
        </div>
      )}
      {avertissement && (
        <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', color: '#E65100', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
          {avertissement}
        </div>
      )}
      <div style={{ background: '#F7F7F7', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>Synthèse à vérifier avant validation</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          {synthese.map(([label, valeur]) => (
            <div key={label}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{valeur ?? '–'}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Commentaire de validation *</label>
        <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Ce que vous avez vérifié, pourquoi cette valeur est correcte…"
          style={{ ...inp, width: '100%', minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit' }} />
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, marginBottom: 18, cursor: 'pointer' }}>
        <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} style={{ marginTop: 3 }} />
        <span>Je confirme avoir vérifié la valeur, la source et la date d'effet.</span>
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
        <button disabled={!peutValider || saving} onClick={() => onConfirmer(commentaire.trim())}
          style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: peutValider ? 'pointer' : 'not-allowed', opacity: (!peutValider || saving) ? 0.5 : 1 }}>
          {saving ? 'Validation…' : 'Valider'}
        </button>
      </div>
    </ModalPaie>
  );
}

// RH-PAIE-2D §6 — distinction reprise/effet : une version REPRISE_EXISTANT avec date d'effet
// historique inconnue affiche sa date de reprise séparément, jamais présentée comme la date
// réelle d'application historique.
function DateEffetAffichage({ item }) {
  const estReprise = item.source === 'REPRISE_EXISTANT' && item.date_effet_historique_inconnue;
  if (estReprise) {
    return (
      <span>
        <span style={{ color: ORANGE_2D, fontWeight: 700, fontSize: 10.5 }}>Effet historique inconnu</span>
        <br />
        <span style={{ fontSize: 11, color: '#999' }}>Repris le {fmtDate2D(item.date_reprise || item.date_debut || item.date_effet)}</span>
      </span>
    );
  }
  return <span>{fmtDate2D(item.date_debut || item.date_effet)}</span>;
}
const ORANGE_2D = '#E65100';
const fmtDate2D = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '–';

// RH-PAIE-2D §12 — fiche de détail d'un paramètre : tout ce que le tableau ne montre plus.
function FicheDetailParametre({ item, onClose }) {
  const { token } = useAuth();
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [historique, setHistorique] = useState([]);
  const [documents, setDocuments] = useState([]);
  useEffect(() => {
    axios.get(`${API}/paie/parametres/${item.code}/historique`, h).then((r) => setHistorique(r.data?.data || [])).catch(() => {});
    axios.get(`${API}/paie/documents/PARAMETRE_VERSION/${item.id}`, h).then((r) => setDocuments(r.data?.data || [])).catch(() => {});
  }, [item.id]); // eslint-disable-line

  const estReprise = item.source === 'REPRISE_EXISTANT' && item.date_effet_historique_inconnue;

  return (
    <ModalPaie titre={`Détail — ${item.libelle}`} onClose={onClose} large>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, fontSize: 13 }}>
        <ChampDetail label="Code technique" valeur={item.code} />
        <ChampDetail label="Version" valeur={item.version} />
        <ChampDetail label="Domaine" valeur={humaniserDomaine(item.domaine)} />
        <ChampDetail label="Unité" valeur={humaniserUnite(item.unite)} />
        <ChampDetail label="Valeur" valeur={item.valeur_numerique ?? (item.valeur_booleenne !== null ? String(item.valeur_booleenne) : '—')} />
        <ChampDetail label="Statut" valeur={<BadgeStatutPaie statut={item.statut} niveauConfiance={item.niveau_confiance} />} />
        <ChampDetail label="Source" valeur={humaniserSource(item.source)} />
        <ChampDetail label="Niveau de validation" valeur={humaniserNiveauConfiance(item.niveau_confiance)} />
        <ChampDetail label="Référence / décision" valeur={item.reference_juridique || '–'} />
        <ChampDetail label="Auteur" valeur={item.auteur_nom ? `${item.auteur_prenom} ${item.auteur_nom}` : '–'} />
        <ChampDetail label="Validateur" valeur={item.validateur_nom ? `${item.validateur_prenom} ${item.validateur_nom}` : '–'} />
        <ChampDetail label="Commentaire de validation" valeur={item.commentaire_validation || '–'} />
      </div>

      <div style={{ background: estReprise ? '#FFF3E0' : '#F7F7F7', border: `1px solid ${estReprise ? '#FFCC80' : GRIS_BORD}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>Dates</div>
        {estReprise ? (
          <>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Date de reprise technique :</strong> {fmtDate2D(item.date_reprise || item.date_debut)}</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Date d'effet métier :</strong> <span style={{ color: ORANGE_2D, fontWeight: 700 }}>Inconnue</span></div>
            <div style={{ fontSize: 12, color: '#E65100', marginTop: 6 }}><AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />Donnée reprise de l'ancien système — la date de reprise n'est pas une preuve de la date réelle d'application historique de cette règle.</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Date d'effet :</strong> {fmtDate2D(item.date_debut)}</div>
            <div style={{ fontSize: 13 }}><strong>Date de fin :</strong> {item.date_fin ? fmtDate2D(item.date_fin) : 'Aucune (en cours)'}</div>
          </>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>Motif</div>
        <div style={{ fontSize: 13, color: '#444' }}>{item.motif || '–'}</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>Pièces justificatives</div>
        {documents.length === 0 ? <div style={{ fontSize: 12.5, color: '#999' }}>Aucune pièce jointe.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {documents.map((d) => (
              <a key={d.id} href={`${API}/paie/documents/${d.id}/fichier`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); axios.get(`${API}/paie/documents/${d.id}/fichier`, { ...h, responseType: 'blob' }).then((r) => { const url = URL.createObjectURL(r.data); window.open(url, '_blank'); }); }}
                style={{ fontSize: 12.5, color: VERT, textDecoration: 'none', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <Paperclip size={13} /> {d.nom_original} — <span style={{ color: '#999' }}>{humaniserTypeDocument(d.type_document)}</span>
              </a>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>Historique des versions ({item.code})</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Version', 'Valeur', 'Date d\'effet', 'Statut'].map((th) => <th key={th} style={{ textAlign: 'left', padding: '6px 8px', color: '#999', fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${GRIS_BORD}` }}>{th}</th>)}</tr></thead>
            <tbody>
              {historique.map((v) => (
                <tr key={v.id} style={{ background: v.id === item.id ? '#F1F8E9' : 'transparent' }}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{v.version}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{v.valeur_numerique ?? '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}><DateEffetAffichage item={v} /></td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}><BadgeStatutPaie statut={v.statut} niveauConfiance={v.niveau_confiance} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ModalPaie>
  );
}
function ChampDetail({ label, valeur }) {
  return <div><div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>{label}</div><div style={{ fontWeight: 600, color: '#222' }}>{valeur}</div></div>;
}

const ParametresPaieSection = () => {
  const { token } = useAuth();
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [qualite, setQualite] = useState(null);
  const [statutFiltre, setStatutFiltre] = useState('');
  const [msg, setMsg] = useState(null);
  const [modalOuvert, setModalOuvert] = useState(false);
  const [form, setForm] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [motifAction, setMotifAction] = useState('');
  const [detailOuvert, setDetailOuvert] = useState(null);
  const [validationOuverte, setValidationOuverte] = useState(null);
  const [erreurValidation, setErreurValidation] = useState(null);
  const [saving, setSaving] = useState(false);

  const charger = () => {
    if (!token) return;
    const params = statutFiltre ? { statut: statutFiltre, limite: 100 } : { limite: 100 };
    axios.get(`${API}/paie/parametres`, { ...h, params }).then((r) => setItems(r.data?.data?.items || [])).catch(() => {});
    axios.get(`${API}/paie/parametres/qualite`, h).then((r) => setQualite(r.data?.data || null)).catch(() => {});
  };
  useEffect(charger, [token, statutFiltre]); // eslint-disable-line

  const notifier = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // RH-PAIE-2D §7 — formulaire de création vide par défaut : aucune valeur métier sensible
  // présélectionnée. La date du jour est seulement une suggestion visuelle non enregistrée
  // tant que l'utilisateur ne l'a pas explicitement choisie.
  const ouvrirCreation = () => {
    setForm({ code: '', libelle: '', domaine: '', type_valeur: '', valeur_numerique: '', unite: '', date_debut: '', source: '', niveau_confiance: '', motif: '' });
    setModalOuvert(true);
  };

  const soumettreCreation = async () => {
    try {
      await axios.post(`${API}/paie/parametres`, form, h);
      setModalOuvert(false);
      notifier('ok', 'Paramètre créé en brouillon.');
      charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  const actionSimple = async (id, action, motif) => {
    try {
      const body = motif !== undefined ? { motif } : {};
      await axios.patch(`${API}/paie/parametres/${id}/${action}`, body, h);
      notifier('ok', 'Action effectuée.');
      charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // RH-PAIE-2D §9 — plus de validation en un clic : ouvre la modale de synthèse.
  const demanderValidation = (p) => { setErreurValidation(null); setValidationOuverte(p); };
  // RH-PAIE-2H — l'échec (ex. permission insuffisante) s'affiche désormais DANS la modale
  // (setErreurValidation), jamais dans la bannière globale : la modale reste ouverte, le
  // commentaire et la case cochée restent intacts. Garde anti-double-appel en tête de fonction.
  const confirmerValidation = async (commentaire) => {
    if (saving) return;
    setSaving(true);
    setErreurValidation(null);
    try {
      await axios.patch(`${API}/paie/parametres/${validationOuverte.id}/valider`, { commentaire, confirmationExplicite: true }, h);
      setValidationOuverte(null);
      notifier('ok', 'Paramètre validé.');
      charger();
    } catch (e) { setErreurValidation(messageErreurValidation(e)); }
    finally { setSaving(false); }
  };

  const formValide = form.code && form.libelle && form.domaine && form.type_valeur && form.date_debut && form.source && form.niveau_confiance && form.motif?.trim();

  return (
    <div>
      {msg && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: msg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: msg.type === 'ok' ? VERT : ROUGE, fontWeight: 600, fontSize: 13 }}>{msg.text}</div>}

      {qualite && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          {[['Brouillons', qualite.brouillons], ['À valider', qualite.a_valider], ['Actifs', qualite.actifs], ['Programmés', qualite.programmes], ['Interdits de calcul', qualite.interdits_de_calcul], ['Chevauchements détectés', qualite.chevauchements_detectes]].map(([label, val]) => (
            <div key={label} style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: '10px 14px', minWidth: 120 }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <select value={statutFiltre} onChange={(e) => setStatutFiltre(e.target.value)} style={{ ...inp, minWidth: 180 }}>
          <option value="">Tous les statuts</option>
          {Object.keys(STATUTS_PAIE_DICT).map((s) => <option key={s} value={s}>{humaniserStatut(s)}</option>)}
        </select>
        <button onClick={ouvrirCreation} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Nouveau paramètre</button>
      </div>

      {/* RH-PAIE-2D §11 — desktop : colonnes essentielles seulement, le reste dans le détail. */}
      <div className="ppv-wrap" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <style>{`
          .ppv-table { width: 100%; border-collapse: collapse; font-size: 12.5px; background: #fff; border: 1px solid ${GRIS_BORD}; border-radius: 10px; }
          .ppv-cards { display: none; }
          @media (max-width: 900px) { .ppv-table { display: none; } .ppv-cards { display: block; } }
        `}</style>
        <table className="ppv-table">
          <thead>
            <tr>
              {['Code', 'Libellé', 'Valeur', 'Date d\'effet', 'Statut', ''].map((th) => (
                <th key={th} style={{ textAlign: 'left', padding: '10px 12px', color: '#888', fontSize: 10.5, textTransform: 'uppercase', borderBottom: `2px solid ${GRIS_BORD}` }}>{th}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>{p.code}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.libelle}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.valeur_numerique ?? (p.valeur_booleenne !== null ? String(p.valeur_booleenne) : '—')} {p.unite ? humaniserUnite(p.unite) : ''}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}><DateEffetAffichage item={p} /></td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}><BadgeStatutPaie statut={p.statut} niveauConfiance={p.niveau_confiance} /></td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => setDetailOuvert(p)} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: 'none', color: '#555', cursor: 'pointer' }}>Voir</button>
                    {p.statut === 'BROUILLON' && <button onClick={() => actionSimple(p.id, 'soumettre')} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${VERT}`, background: 'none', color: VERT, cursor: 'pointer' }}>Soumettre</button>}
                    {p.statut === 'A_VALIDER' && <>
                      <button onClick={() => demanderValidation(p)} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: VERT, color: '#fff', cursor: 'pointer' }}>Valider</button>
                      <button onClick={() => { setMotifAction(''); setConfirmAction({ id: p.id, action: 'rejeter', titre: 'Rejeter ce paramètre' }); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${ROUGE}`, background: 'none', color: ROUGE, cursor: 'pointer' }}>Rejeter</button>
                    </>}
                    {['VALIDE', 'PROGRAMME', 'ACTIF'].includes(p.statut) && (
                      <button onClick={() => { setMotifAction(''); setConfirmAction({ id: p.id, action: 'archiver', titre: 'Archiver ce paramètre' }); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: 'none', color: '#666', cursor: 'pointer' }}>Archiver</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#999' }}>Aucun paramètre.</td></tr>}
          </tbody>
        </table>

        <div className="ppv-cards">
          {items.map((p) => (
            <div key={p.id} style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <strong style={{ fontSize: 13 }}>{p.code}</strong>
                <BadgeStatutPaie statut={p.statut} niveauConfiance={p.niveau_confiance} />
              </div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{p.libelle}</div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{p.valeur_numerique ?? '—'} {p.unite ? humaniserUnite(p.unite) : ''} · <DateEffetAffichage item={p} /></div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => setDetailOuvert(p)} style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: 'none', color: '#555', cursor: 'pointer' }}>Voir le détail</button>
                {p.statut === 'A_VALIDER' && <button onClick={() => demanderValidation(p)} style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: 'none', background: VERT, color: '#fff', cursor: 'pointer' }}>Valider</button>}
              </div>
            </div>
          ))}
          {items.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>Aucun paramètre.</div>}
        </div>
      </div>

      {modalOuvert && (
        <ModalPaie titre="Nouveau paramètre de paie" onClose={() => setModalOuvert(false)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Code technique (MAJUSCULES_SNAKE) *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /><div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Identifiant stable utilisé par le système.</div></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Libellé *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Domaine *</label><SelectHumanise value={form.domaine} onChange={(v) => setForm({ ...form, domaine: v })} dictionnaire={DOMAINES_PAIE} placeholder="Sélectionner un domaine" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Nature du paramètre *</label><SelectHumanise value={form.type_valeur} onChange={(v) => setForm({ ...form, type_valeur: v })} dictionnaire={TYPES_VALEUR_PARAMETRE} placeholder="Sélectionner une nature" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Valeur numérique</label><input type="number" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.valeur_numerique} onChange={(e) => setForm({ ...form, valeur_numerique: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Unité</label><SelectHumanise value={form.unite} onChange={(v) => setForm({ ...form, unite: v })} dictionnaire={UNITES_PAIE} placeholder="Sélectionner une unité" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date d'effet * <span style={{ fontWeight: 400, color: '#999' }}>(suggestion : {new Date().toLocaleDateString('fr-FR')})</span></label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /><div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Date métier réelle d'application — jamais enregistrée automatiquement.</div></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date de fin</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date_fin || ''} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /><div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Origine de la règle.</div></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={form.niveau_confiance} onChange={(v) => setForm({ ...form, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /><div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Autorité devant confirmer la règle.</div></div>
            <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 12, fontWeight: 600 }}>Référence ou document justificatif</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.reference_juridique || ''} onChange={(e) => setForm({ ...form, reference_juridique: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
            <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setModalOuvert(false)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button disabled={!formValide} onClick={soumettreCreation} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: formValide ? 'pointer' : 'not-allowed', opacity: formValide ? 1 : 0.5 }}>Créer en brouillon</button>
          </div>
        </ModalPaie>
      )}

      {detailOuvert && <FicheDetailParametre item={detailOuvert} onClose={() => setDetailOuvert(null)} />}

      {validationOuverte && (
        <ModaleValidationSecurisee
          titre={`Valider — ${validationOuverte.libelle}`}
          saving={saving}
          avertissement={validationOuverte.source === 'REPRISE_EXISTANT' && validationOuverte.date_effet_historique_inconnue
            ? 'Donnée reprise de l\'ancien système — la date d\'effet historique est inconnue. Vérifiez que cette valeur est réellement correcte avant de valider.'
            : null}
          synthese={[
            ['Code', validationOuverte.code], ['Libellé', validationOuverte.libelle],
            ['Valeur', `${validationOuverte.valeur_numerique ?? '—'} ${validationOuverte.unite ? humaniserUnite(validationOuverte.unite) : ''}`],
            ['Domaine', humaniserDomaine(validationOuverte.domaine)],
            ['Date d\'effet', <DateEffetAffichage item={validationOuverte} />],
            ['Source', humaniserSource(validationOuverte.source)],
            ['Niveau de validation', humaniserNiveauConfiance(validationOuverte.niveau_confiance)],
            ['Référence', validationOuverte.reference_juridique || '–'],
            ['Motif (auteur)', validationOuverte.motif],
          ]}
          erreur={erreurValidation}
          onClose={() => setValidationOuverte(null)}
          onConfirmer={confirmerValidation}
        />
      )}

      {confirmAction && (
        <ModalPaie titre={confirmAction.titre} onClose={() => setConfirmAction(null)}>
          <textarea placeholder="Motif (obligatoire)" value={motifAction} onChange={(e) => setMotifAction(e.target.value)} style={{ ...inp, width: '100%', minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 14 }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={() => setConfirmAction(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button disabled={!motifAction.trim()} onClick={() => { actionSimple(confirmAction.id, confirmAction.action, motifAction); setConfirmAction(null); }} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: ROUGE, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: motifAction.trim() ? 1 : 0.5 }}>Confirmer</button>
          </div>
        </ModalPaie>
      )}
    </div>
  );
};

// RH-PAIE-2C §5-7 — Grille salariale. Réutilise entièrement rh.paie_parametres_versions (même
// table, même moteur de résolution/validation que ParametresPaieSection ci-dessus) sous le
// code unique SALAIRE_CATEGORIEL_BASE, une ligne par catégorie(+échelon)+période. Ce n'est pas
// un second système de paramètres : seule la présentation (colonnes catégorie/échelon/salaire
// plutôt que code/libellé générique) est spécialisée pour cet usage.
const CODE_GRILLE = 'SALAIRE_CATEGORIEL_BASE';

// RH-PAIE-2D §16, RH-PAIE-2E §4 — une ligne éditable pour la saisie en masse (Mode A). La
// famille professionnelle est la première clé de la ligne : catégorie et échelon dépendent
// d'elle et se réinitialisent si elle change.
function LigneSaisieMasse({ ligne, onChange, onSupprimer, categories }) {
  const cat = categories.find((c) => c.code === ligne.categorie && c.famille_professionnelle === ligne.famille_professionnelle);
  const echelons = cat?.echelons_disponibles || [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr 0.8fr 1fr auto', gap: 8, alignItems: 'end', marginBottom: 8, padding: 8, background: '#FAFAFA', borderRadius: 8 }}>
      <SelectHumanise value={ligne.famille_professionnelle} onChange={(v) => onChange({ ...ligne, famille_professionnelle: v, categorie: '', echelon: '' })} dictionnaire={FAMILLES_PROFESSIONNELLES} placeholder="Famille *" />
      <CategorieSelector value={ligne.categorie} onChange={(v) => onChange({ ...ligne, categorie: v, echelon: '' })} allowEmpty={false} disabled={!ligne.famille_professionnelle} familleProfessionnelle={ligne.famille_professionnelle || undefined} />
      <select style={{ ...inp, width: '100%' }} value={ligne.echelon || ''} onChange={(e) => onChange({ ...ligne, echelon: e.target.value })} disabled={echelons.length === 0}>
        <option value="">Non applicable</option>
        {echelons.map((e) => <option key={e} value={e}>{e}</option>)}
      </select>
      <input type="number" placeholder="Salaire de base (FCFA)" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={ligne.valeur_numerique || ''} onChange={(e) => onChange({ ...ligne, valeur_numerique: e.target.value })} />
      <button onClick={onSupprimer} style={{ background: 'none', border: `1px solid ${ROUGE}`, color: ROUGE, borderRadius: 6, padding: '8px 10px', cursor: 'pointer' }}>×</button>
    </div>
  );
}

// RH-PAIE-2D §18, RH-PAIE-2E §4 — tableau de comparaison avant/après, réutilisé par
// duplication et revalorisation. Affiche désormais la famille professionnelle de chaque ligne.
function TableauComparaison({ lignes, onChangerValeur }) {
  const total = lignes.length;
  const categoriesConcernees = [...new Set(lignes.map((l) => `${l.famille_professionnelle}|${l.categorie}`))].length;
  const totalDossiers = lignes.reduce((s, l) => s + (l.dossiers_concernes || 0), 0);
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12.5, color: '#555', marginBottom: 10, flexWrap: 'wrap' }}>
        <span><strong>{total}</strong> ligne(s)</span>
        <span><strong>{categoriesConcernees}</strong> catégorie(s) concernée(s)</span>
        <span><strong>{totalDossiers}</strong> dossier(s) salarial(aux) actuellement sur ces combinaisons</span>
      </div>
      {/* RH-PAIE-2I §10 — jamais laisser croire qu'un dossier salarié existant serait modifié
          par cette opération : seule une nouvelle ligne de grille en BROUILLON est créée. */}
      <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', color: '#E65100', borderRadius: 8, padding: '8px 12px', fontSize: 11.5, fontWeight: 600, marginBottom: 10 }}>
        Aucun dossier salarial existant n'est modifié rétroactivement par cette opération — seules de nouvelles lignes de grille en brouillon sont créées, à valider séparément.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr>{['Famille', 'Catégorie', 'Échelon', 'Ancienne valeur', 'Nouvelle valeur', 'Écart FCFA', 'Écart %', 'Dossiers concernés'].map((th) => <th key={th} style={{ textAlign: 'left', padding: '6px 8px', color: '#999', fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${GRIS_BORD}` }}>{th}</th>)}</tr></thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{humaniserFamilleProfessionnelle(l.famille_professionnelle)}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>{l.categorie}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{l.echelon || 'Non applicable'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{Number(l.ancienne_valeur).toLocaleString('fr-FR')} FCFA</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                  {onChangerValeur ? (
                    <input type="number" value={l.nouvelle_valeur} onChange={(e) => onChangerValeur(i, e.target.value)} style={{ ...inp, width: 120 }} />
                  ) : `${Number(l.nouvelle_valeur).toLocaleString('fr-FR')} FCFA`}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', color: l.ecart_fcfa > 0 ? VERT : l.ecart_fcfa < 0 ? ROUGE : '#666' }}>{l.ecart_fcfa > 0 ? '+' : ''}{Number(l.nouvelle_valeur - l.ancienne_valeur).toLocaleString('fr-FR')}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{l.ecart_pourcent ? `${l.ecart_pourcent > 0 ? '+' : ''}${l.ecart_pourcent}%` : '–'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{l.dossiers_concernes || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const GrilleSalarialeSection = () => {
  const { token, employe, hasPermission } = useAuth();
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [selection, setSelection] = useState([]);
  const [statutFiltre, setStatutFiltre] = useState('');
  const [msg, setMsg] = useState(null);
  const [mode, setMode] = useState(null); // null | 'unitaire' | 'masse' | 'duplication' | 'revalorisation' | 'import'
  const [form, setForm] = useState({});
  const [categories, setCategories] = useState([]);
  const [ancienneValeur, setAncienneValeur] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [motifAction, setMotifAction] = useState('');
  const [validationLotOuverte, setValidationLotOuverte] = useState(false);
  const [erreurValidationLot, setErreurValidationLot] = useState(null);
  const [saving, setSaving] = useState(false);
  // RH-PAIE-2H — la validation d'une entrée de grille salariale est réservée au rôle DRH dans
  // tout le module (même règle que les paramètres et rubriques de paie, voir
  // GUIDE-RECETTE-VISUELLE-RH-PAIE-1-2.md §5 et backend/services/paieParametreService.js
  // ROLES_VALIDATION_AUTO). Le bouton de validation ne doit donc s'afficher que pour un
  // utilisateur qui pourra réellement effectuer l'action — jamais une action que le backend
  // refusera systématiquement. Reproduit exactement la règle backend (rôle OU permission
  // explicite accordée au poste/à l'individu), jamais codée en dur au-delà de ce miroir.
  const peutValiderGrille = employe?.role === 'drh' || hasPermission('peut_valider_parametre_paie');

  // États des modes de masse
  const [lignesMasse, setLignesMasse] = useState([{ famille_professionnelle: '', categorie: '', echelon: '', valeur_numerique: '' }]);
  const [dateDupSource, setDateDupSource] = useState('');
  const [dateDupEffet, setDateDupEffet] = useState('');
  const [apercuDup, setApercuDup] = useState(null);
  const [dateRevalSource, setDateRevalSource] = useState('');
  const [dateRevalEffet, setDateRevalEffet] = useState('');
  const [pourcentageGlobal, setPourcentageGlobal] = useState('');
  const [arrondi, setArrondi] = useState('CENTAINE');
  const [apercuReval, setApercuReval] = useState(null);
  const [fichierImport, setFichierImport] = useState(null);
  const [apercuImport, setApercuImport] = useState(null);
  const [fichierNom, setFichierNom] = useState('');

  const charger = () => {
    if (!token) return;
    const params = { domaine: 'GRILLE_SALARIALE', limite: 200 };
    if (statutFiltre) params.statut = statutFiltre;
    axios.get(`${API}/paie/parametres`, { ...h, params }).then((r) => setItems(r.data?.data?.items || [])).catch(() => {});
  };
  useEffect(charger, [token, statutFiltre]); // eslint-disable-line
  useEffect(() => { chargerCategories().then(setCategories); }, []);

  const notifier = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // ─── Mode : saisie unitaire (corrigé §23 : rien de présélectionné ; §4 : famille en premier) ───
  const ouvrirCreationUnitaire = () => {
    setForm({ famille_professionnelle: '', categorie: '', echelon: '', valeur_numerique: '', date_debut: '', date_fin: '', source: '', niveau_confiance: '', reference_juridique: '', motif: '' });
    setAncienneValeur(null);
    setMode('unitaire');
  };
  useEffect(() => {
    if (mode !== 'unitaire' || !form.famille_professionnelle || !form.categorie || !form.date_debut) { setAncienneValeur(null); return; }
    let annule = false;
    axios.get(`${API}/paie/parametres/${CODE_GRILLE}/resoudre`, { ...h, params: { date_reference: form.date_debut, famille_professionnelle: form.famille_professionnelle, categorie: form.categorie, echelon: form.echelon || undefined } })
      .then(({ data }) => { if (!annule) setAncienneValeur(data.data || null); }).catch(() => { if (!annule) setAncienneValeur(null); });
    return () => { annule = true; };
  }, [mode, form.famille_professionnelle, form.categorie, form.echelon, form.date_debut]); // eslint-disable-line

  const soumettreCreationUnitaire = async () => {
    try {
      const categorieLabel = categories.find((c) => c.code === form.categorie && c.famille_professionnelle === form.famille_professionnelle)?.libelle || form.categorie;
      const payload = {
        code: CODE_GRILLE, libelle: `Salaire catégoriel de base — ${humaniserFamilleProfessionnelle(form.famille_professionnelle)} ${categorieLabel}${form.echelon ? ' (échelon ' + form.echelon + ')' : ''}`,
        domaine: 'GRILLE_SALARIALE', type_valeur: 'MONTANT', valeur_numerique: form.valeur_numerique,
        famille_professionnelle: form.famille_professionnelle,
        categorie: form.categorie, echelon: form.echelon || undefined, date_debut: form.date_debut, date_fin: form.date_fin || undefined,
        source: form.source, niveau_confiance: form.niveau_confiance, reference_juridique: form.reference_juridique || undefined, motif: form.motif,
      };
      await axios.post(`${API}/paie/parametres`, payload, h);
      setMode(null); notifier('ok', 'Entrée de grille créée en brouillon.'); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // ─── Mode A : saisie en masse ───
  const soumettreMasse = async () => {
    try {
      if (!form.date_debut || !form.source || !form.niveau_confiance || !form.motif?.trim()) {
        notifier('err', 'Date d\'effet, source, niveau de validation et motif sont obligatoires pour le lot.'); return;
      }
      const lignes = lignesMasse.filter((l) => l.famille_professionnelle && l.categorie && l.valeur_numerique).map((l) => ({ ...l, date_debut: form.date_debut, source: form.source, niveau_confiance: form.niveau_confiance, motif: form.motif, reference_juridique: form.reference_juridique }));
      await axios.post(`${API}/paie/grille/lignes`, { lignes }, h);
      setMode(null); setLignesMasse([{ famille_professionnelle: '', categorie: '', echelon: '', valeur_numerique: '' }]); notifier('ok', `${lignes.length} ligne(s) créée(s) en brouillon.`); charger();
    } catch (e) { notifier('err', e.response?.data?.message || (e.response?.data?.details ? `Ligne ${e.response.data.details[0].ligne} : ${e.response.data.details[0].erreurs.join(', ')}` : 'Erreur')); }
  };

  // ─── Mode B : duplication ───
  const previsualiserDup = async () => {
    try {
      const { data } = await axios.get(`${API}/paie/grille/duplication/apercu`, { ...h, params: { date_reference: dateDupSource, nouvelle_date_effet: dateDupEffet } });
      setApercuDup(data.data);
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };
  const confirmerDup = async () => {
    try {
      if (!form.source || !form.niveau_confiance || !form.motif?.trim()) { notifier('err', 'Source, niveau de validation et motif sont obligatoires.'); return; }
      await axios.post(`${API}/paie/grille/duplication`, { nouvelleDateEffet: dateDupEffet, source: form.source, niveau_confiance: form.niveau_confiance, motif: form.motif, lignes: apercuDup }, h);
      setMode(null); setApercuDup(null); notifier('ok', 'Grille dupliquée en brouillon.'); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // ─── Mode C : revalorisation en pourcentage ───
  const previsualiserReval = async () => {
    try {
      const { data } = await axios.get(`${API}/paie/grille/revalorisation/apercu`, { ...h, params: { date_reference: dateRevalSource, pourcentage_global: pourcentageGlobal, arrondi } });
      setApercuReval(data.data);
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };
  const confirmerReval = async () => {
    try {
      if (!form.source || !form.niveau_confiance || !form.motif?.trim()) { notifier('err', 'Source, niveau de validation et motif sont obligatoires.'); return; }
      await axios.post(`${API}/paie/grille/revalorisation`, { nouvelleDateEffet: dateRevalEffet, source: form.source, niveau_confiance: form.niveau_confiance, motif: form.motif, lignes: apercuReval }, h);
      setMode(null); setApercuReval(null); notifier('ok', 'Revalorisation créée en brouillon.'); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // ─── Mode D : import Excel ───
  // RH-PAIE-2E §6 — le token JWT ne doit jamais transiter par l'URL (historique navigateur,
  // logs Nginx, nom de fichier) : téléchargement authentifié via le client HTTP existant
  // (même pattern que les pièces justificatives et le modèle d'import employés).
  const telechargerModele = async () => {
    try {
      const res = await axios.get(`${API}/paie/grille/import/modele`, { ...h, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'modele-grille-salariale.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifier('err', 'Impossible de télécharger le modèle.');
    }
  };
  const analyserFichier = async () => {
    if (!fichierImport) return;
    try {
      const fd = new FormData(); fd.append('fichier', fichierImport);
      const { data } = await axios.post(`${API}/paie/grille/import/apercu`, fd, { headers: { ...h.headers, 'Content-Type': 'multipart/form-data' } });
      setApercuImport(data.data);
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };
  const confirmerImportFichier = async () => {
    try {
      const lignesValides = apercuImport.apercu.filter((l) => l.erreurs.length === 0).map((l) => l.valeurs);
      await axios.post(`${API}/paie/grille/import/confirmer`, { lignes: lignesValides }, h);
      setMode(null); setApercuImport(null); setFichierImport(null); setFichierNom(''); notifier('ok', `${lignesValides.length} ligne(s) importée(s) en brouillon.`); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // ─── Actions unitaires + validation en lot (§19) ───
  const actionSimple = async (id, action, motif) => {
    try {
      const body = motif !== undefined ? { motif } : {};
      await axios.patch(`${API}/paie/parametres/${id}/${action}`, body, h);
      notifier('ok', 'Action effectuée.'); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };
  const basculerSelection = (id) => setSelection((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  // RH-PAIE-2H — erreur affichée dans la modale (setErreurValidationLot), jamais dans la bannière
  // globale : la modale reste ouverte, le commentaire et la case cochée restent intacts. Garde
  // anti-double-appel en tête de fonction (en plus du bouton déjà désactivé pendant `saving`).
  const confirmerValidationLot = async (commentaire) => {
    if (saving) return;
    setSaving(true);
    setErreurValidationLot(null);
    try {
      await axios.patch(`${API}/paie/grille/valider-lot`, { ids: selection, commentaire, confirmationExplicite: true }, h);
      setValidationLotOuverte(false); setSelection([]); notifier('ok', `${selection.length} ligne(s) validée(s).`); charger();
    } catch (e) { setErreurValidationLot(messageErreurValidation(e)); }
    finally { setSaving(false); }
  };

  const categorieSelectionnee = categories.find((c) => c.code === form.categorie && c.famille_professionnelle === form.famille_professionnelle);
  const echelonsDisponibles = categorieSelectionnee?.echelons_disponibles || [];
  const itemsAValider = items.filter((i) => i.statut === 'A_VALIDER');

  return (
    <div>
      <p style={{ fontSize: 12.5, color: '#666', marginTop: -6, marginBottom: 16 }}>
        Le salaire de base catégoriel appliqué automatiquement lors de la création d'un dossier salarial ou d'une promotion.
        Une nouvelle grille avec une nouvelle date d'effet remplace l'ancienne à partir de cette date — l'ancienne reste
        consultable dans l'historique, jamais modifiée après validation.
      </p>
      {msg && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: msg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: msg.type === 'ok' ? VERT : ROUGE, fontWeight: 600, fontSize: 13 }}>{msg.text}</div>}

      {/* RH-PAIE-2D §16-17 — modes de gestion de masse */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={ouvrirCreationUnitaire} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>+ Saisie unitaire</button>
        <button onClick={() => { setForm({ date_debut: '', source: '', niveau_confiance: '', motif: '', reference_juridique: '' }); setLignesMasse([{ famille_professionnelle: '', categorie: '', echelon: '', valeur_numerique: '' }]); setMode('masse'); }} style={{ background: '#fff', color: VERT, border: `1px solid ${VERT}`, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Saisie multiple</button>
        <button onClick={() => { setDateDupSource(new Date().toISOString().slice(0, 10)); setDateDupEffet(''); setApercuDup(null); setForm({ source: '', niveau_confiance: '', motif: '' }); setMode('duplication'); }} style={{ background: '#fff', color: VERT, border: `1px solid ${VERT}`, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Dupliquer une grille</button>
        <button onClick={() => { setDateRevalSource(new Date().toISOString().slice(0, 10)); setDateRevalEffet(''); setPourcentageGlobal(''); setApercuReval(null); setForm({ source: '', niveau_confiance: '', motif: '' }); setMode('revalorisation'); }} style={{ background: '#fff', color: VERT, border: `1px solid ${VERT}`, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Revaloriser en %</button>
        <button onClick={() => { setFichierImport(null); setFichierNom(''); setApercuImport(null); setMode('import'); }} style={{ background: '#fff', color: VERT, border: `1px solid ${VERT}`, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Importer Excel</button>
        {selection.length > 0 && (
          peutValiderGrille ? (
            <button onClick={() => { setErreurValidationLot(null); setValidationLotOuverte(true); }} style={{ background: ORANGE_2D, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}>Valider la sélection ({selection.length})</button>
          ) : (
            // RH-PAIE-2H — jamais un bouton actif menant systématiquement à un refus backend :
            // la validation est réservée au DRH, on l'indique clairement au lieu de le proposer.
            <span title="La validation d'une entrée de grille salariale est réservée au rôle DRH." style={{ marginLeft: 'auto', fontSize: 12, color: '#888', background: '#F0F0F0', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, padding: '8px 14px', fontWeight: 600 }}>
              Validation réservée au DRH ({selection.length} sélectionnée{selection.length > 1 ? 's' : ''})
            </span>
          )
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <select value={statutFiltre} onChange={(e) => setStatutFiltre(e.target.value)} style={{ ...inp, minWidth: 180 }}>
          <option value="">Tous les statuts</option>
          {Object.keys(STATUTS_PAIE_DICT).map((s) => <option key={s} value={s}>{humaniserStatut(s)}</option>)}
        </select>
      </div>

      <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10 }}>
          <thead>
            <tr>
              {['', 'Famille', 'Catégorie', 'Échelon', 'Salaire de base', 'Date de début', 'Date de fin', 'Version', 'Statut', ''].map((th) => (
                <th key={th} style={{ textAlign: 'left', padding: '10px 12px', color: '#888', fontSize: 10.5, textTransform: 'uppercase', borderBottom: `2px solid ${GRIS_BORD}` }}>{th}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>
                  {p.statut === 'A_VALIDER' && <input type="checkbox" checked={selection.includes(p.id)} onChange={() => basculerSelection(p.id)} />}
                </td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.famille_professionnelle ? humaniserFamilleProfessionnelle(p.famille_professionnelle) : '–'}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>{p.categorie || '–'}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.echelon || 'Non applicable'}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.valeur_numerique ? Number(p.valeur_numerique).toLocaleString('fr-FR') + ' FCFA' : '–'}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}><DateEffetAffichage item={p} /></td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.date_fin ? fmtDate2D(p.date_fin) : '–'}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{p.version}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}><BadgeStatutPaie statut={p.statut} niveauConfiance={p.niveau_confiance} /></td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {p.statut === 'BROUILLON' && <button onClick={() => actionSimple(p.id, 'soumettre')} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${VERT}`, background: 'none', color: VERT, cursor: 'pointer' }}>Soumettre</button>}
                    {p.statut === 'A_VALIDER' && (
                      <button onClick={() => { setMotifAction(''); setConfirmAction({ id: p.id, action: 'rejeter', titre: 'Rejeter cette entrée de grille' }); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${ROUGE}`, background: 'none', color: ROUGE, cursor: 'pointer' }}>Rejeter</button>
                    )}
                    {['VALIDE', 'PROGRAMME', 'ACTIF'].includes(p.statut) && (
                      <button onClick={() => { setMotifAction(''); setConfirmAction({ id: p.id, action: 'archiver', titre: 'Archiver cette entrée de grille' }); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: 'none', color: '#666', cursor: 'pointer' }}>Archiver</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#999' }}>Aucune entrée de grille.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ─── Mode unitaire ─── */}
      {mode === 'unitaire' && (
        <ModalPaie titre="Nouvelle entrée de grille salariale" onClose={() => setMode(null)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Famille professionnelle *</label>
              <SelectHumanise value={form.famille_professionnelle} onChange={(v) => setForm({ ...form, famille_professionnelle: v, categorie: '', echelon: '' })} dictionnaire={FAMILLES_PROFESSIONNELLES} placeholder="Sélectionner une famille" />
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Un même code de catégorie peut exister dans plusieurs familles avec des salaires différents — la famille doit être choisie en premier.</div>
            </div>
            <CategorieSelector label="Catégorie *" value={form.categorie} onChange={(v) => setForm({ ...form, categorie: v, echelon: '' })} allowEmpty={false} disabled={!form.famille_professionnelle} familleProfessionnelle={form.famille_professionnelle || undefined} />
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Échelon</label>
              <select style={{ ...inp, width: '100%' }} value={form.echelon || ''} onChange={(e) => setForm({ ...form, echelon: e.target.value })} disabled={echelonsDisponibles.length === 0}>
                <option value="">Non applicable</option>
                {echelonsDisponibles.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Salaire de base (FCFA) *</label><input type="number" min="0" step="1" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.valeur_numerique} onChange={(e) => setForm({ ...form, valeur_numerique: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date d'effet *</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date de fin (optionnelle)</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date_fin || ''} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={form.niveau_confiance} onChange={(v) => setForm({ ...form, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /></div>
            <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 12, fontWeight: 600 }}>Référence ou décision SOGAS</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.reference_juridique || ''} onChange={(e) => setForm({ ...form, reference_juridique: e.target.value })} /></div>
          </div>

          {form.categorie && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: '#f7f7f7', fontSize: 12.5 }}>
              <strong>Valeur actuellement applicable pour ce contexte à cette date : </strong>
              {ancienneValeur ? `${Number(ancienneValeur.valeur_numerique).toLocaleString('fr-FR')} FCFA (version ${ancienneValeur.version})` : 'aucune — première entrée pour cette catégorie/échelon.'}
              {form.valeur_numerique && (<div style={{ marginTop: 4 }}>→ Nouvelle valeur proposée : <strong style={{ color: VERT }}>{Number(form.valeur_numerique).toLocaleString('fr-FR')} FCFA</strong></div>)}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
            <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setMode(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button disabled={!form.famille_professionnelle || !form.categorie || !form.valeur_numerique || !form.date_debut || !form.source || !form.niveau_confiance || !form.motif?.trim()}
              onClick={soumettreCreationUnitaire}
              style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (!form.famille_professionnelle || !form.categorie || !form.valeur_numerique || !form.date_debut || !form.source || !form.niveau_confiance || !form.motif?.trim()) ? 0.5 : 1 }}>
              Créer en brouillon
            </button>
          </div>
        </ModalPaie>
      )}

      {/* ─── Mode A : saisie multiple ─── */}
      {mode === 'masse' && (
        <ModalPaie titre="Saisie multiple de la grille" onClose={() => setMode(null)} large>
          <p style={{ fontSize: 12, color: '#777', marginTop: -4 }}>Chaque ligne devient une entrée de grille en brouillon. Date d'effet, source, niveau et motif s'appliquent à tout le lot.</p>
          {lignesMasse.map((l, i) => (
            <LigneSaisieMasse key={i} ligne={l} categories={categories}
              onChange={(nv) => setLignesMasse((ls) => ls.map((x, idx) => idx === i ? nv : x))}
              onSupprimer={() => setLignesMasse((ls) => ls.filter((_, idx) => idx !== i))} />
          ))}
          <button onClick={() => setLignesMasse((ls) => [...ls, { famille_professionnelle: '', categorie: '', echelon: '', valeur_numerique: '' }])} style={{ background: 'none', border: `1px dashed ${VERT}`, color: VERT, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>+ Ajouter une ligne</button>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date d'effet *</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={form.niveau_confiance} onChange={(v) => setForm({ ...form, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Référence</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.reference_juridique || ''} onChange={(e) => setForm({ ...form, reference_juridique: e.target.value })} /></div>
          </div>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
          <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={form.motif || ''} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setMode(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button onClick={soumettreMasse} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Créer les lignes en brouillon</button>
          </div>
        </ModalPaie>
      )}

      {/* ─── Mode B : duplication ─── */}
      {mode === 'duplication' && (
        <ModalPaie titre="Dupliquer une grille existante" onClose={() => setMode(null)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Grille de référence (à la date du)</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={dateDupSource} onChange={(e) => setDateDupSource(e.target.value)} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Nouvelle date d'effet *</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={dateDupEffet} onChange={(e) => setDateDupEffet(e.target.value)} /></div>
          </div>
          <button onClick={previsualiserDup} disabled={!dateDupEffet} style={{ background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>Prévisualiser</button>
          {apercuDup && (
            <>
              <TableauComparaison lignes={apercuDup} onChangerValeur={(i, v) => setApercuDup((ls) => ls.map((l, idx) => idx === i ? { ...l, nouvelle_valeur: v } : l))} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '14px 0' }}>
                <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /></div>
                <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={form.niveau_confiance} onChange={(v) => setForm({ ...form, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /></div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
              <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={form.motif || ''} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setMode(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            {apercuDup && <button onClick={confirmerDup} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Créer en brouillon</button>}
          </div>
        </ModalPaie>
      )}

      {/* ─── Mode C : revalorisation ─── */}
      {mode === 'revalorisation' && (
        <ModalPaie titre="Revaloriser la grille en pourcentage" onClose={() => setMode(null)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Grille de référence (à la date du)</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={dateRevalSource} onChange={(e) => setDateRevalSource(e.target.value)} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Nouvelle date d'effet *</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={dateRevalEffet} onChange={(e) => setDateRevalEffet(e.target.value)} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Pourcentage global *</label><input type="number" step="0.1" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={pourcentageGlobal} onChange={(e) => setPourcentageGlobal(e.target.value)} placeholder="Ex : 5 pour +5%" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Règle d'arrondi</label><SelectHumanise value={arrondi} onChange={setArrondi} dictionnaire={ARRONDIS_GRILLE} placeholder="Aucun arrondi" /></div>
          </div>
          <button onClick={previsualiserReval} disabled={!dateRevalEffet || !pourcentageGlobal} style={{ background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>Prévisualiser</button>
          {apercuReval && (
            <>
              <TableauComparaison lignes={apercuReval} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '14px 0' }}>
                <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /></div>
                <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={form.niveau_confiance} onChange={(v) => setForm({ ...form, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /></div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
              <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={form.motif || ''} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setMode(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            {apercuReval && <button onClick={confirmerReval} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Créer en brouillon</button>}
          </div>
        </ModalPaie>
      )}

      {/* ─── Mode D : import Excel ─── */}
      {mode === 'import' && (
        <ModalPaie titre="Importer une grille depuis un fichier Excel" onClose={() => setMode(null)} large>
          <button onClick={telechargerModele} style={{ background: 'none', border: `1px solid ${GRIS_BORD}`, color: '#555', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>Télécharger le modèle</button>
          <div style={{ marginBottom: 12 }}>
            <input type="file" accept=".xlsx" onChange={(e) => { setFichierImport(e.target.files[0]); setFichierNom(e.target.files[0]?.name || ''); setApercuImport(null); }} />
            {fichierNom && <span style={{ fontSize: 12, marginLeft: 8 }}>{fichierNom}</span>}
          </div>
          <button onClick={analyserFichier} disabled={!fichierImport} style={{ background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>Analyser le fichier</button>
          {apercuImport && (
            <>
              <div style={{ display: 'flex', gap: 14, fontSize: 12.5, marginBottom: 10 }}>
                <span>{apercuImport.total_lignes} ligne(s)</span>
                <span style={{ color: VERT }}>{apercuImport.lignes_valides} valide(s)</span>
                <span style={{ color: ROUGE }}>{apercuImport.lignes_invalides} invalide(s)</span>
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>{['Ligne', 'Famille', 'Catégorie', 'Salaire', 'Erreurs'].map((th) => <th key={th} style={{ textAlign: 'left', padding: 6, color: '#999', fontSize: 10, borderBottom: `1px solid ${GRIS_BORD}` }}>{th}</th>)}</tr></thead>
                  <tbody>
                    {apercuImport.apercu.map((l) => (
                      <tr key={l.ligne} style={{ background: l.erreurs.length ? '#FFEBEE' : 'transparent' }}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.ligne}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.famille_professionnelle ? humaniserFamilleProfessionnelle(l.famille_professionnelle) : '–'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.categorie || '–'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.valeurs?.salaire_base_fcfa || '–'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0', color: ROUGE }}>{l.erreurs.join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {apercuImport.lignes_invalides > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: ORANGE_2D }}>Les lignes en erreur seront ignorées ; seules les {apercuImport.lignes_valides} ligne(s) valide(s) seront importées.</div>
              )}
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setMode(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            {apercuImport && apercuImport.lignes_valides > 0 && <button onClick={confirmerImportFichier} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Importer {apercuImport.lignes_valides} ligne(s) en brouillon</button>}
          </div>
        </ModalPaie>
      )}

      {/* ─── Validation en lot (§19) ─── */}
      {validationLotOuverte && (
        <ModaleValidationSecurisee
          titre={`Valider ${selection.length} ligne(s) de grille`}
          saving={saving}
          synthese={items.filter((i) => selection.includes(i.id)).map((i) => [`${humaniserFamilleProfessionnelle(i.famille_professionnelle)} — ${i.categorie}${i.echelon ? ' (' + i.echelon + ')' : ''}`, `${Number(i.valeur_numerique).toLocaleString('fr-FR')} FCFA — ${fmtDate2D(i.date_debut)}`])}
          erreur={erreurValidationLot}
          onClose={() => setValidationLotOuverte(false)}
          onConfirmer={confirmerValidationLot}
        />
      )}

      {confirmAction && (
        <ModalPaie titre={confirmAction.titre} onClose={() => setConfirmAction(null)}>
          <textarea placeholder="Motif (obligatoire)" value={motifAction} onChange={(e) => setMotifAction(e.target.value)} style={{ ...inp, width: '100%', minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 14 }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={() => setConfirmAction(null)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button disabled={!motifAction.trim()} onClick={() => { actionSimple(confirmAction.id, confirmAction.action, motifAction); setConfirmAction(null); }} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: ROUGE, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: motifAction.trim() ? 1 : 0.5 }}>Confirmer</button>
          </div>
        </ModalPaie>
      )}
      {itemsAValider.length === 0 && null}
    </div>
  );
};

const RubriquesPaieSection = () => {
  const { token } = useAuth();
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [qualite, setQualite] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [typeFiltre, setTypeFiltre] = useState('');
  const [familleFiltre, setFamilleFiltre] = useState('');
  const [statutFiltre, setStatutFiltre] = useState('');
  const [msg, setMsg] = useState(null);
  const [modalOuvert, setModalOuvert] = useState(false);
  const [form, setForm] = useState({});
  const [rubriqueOuverte, setRubriqueOuverte] = useState(null);
  const [versions, setVersions] = useState([]);
  const [modalVersion, setModalVersion] = useState(false);
  const [formVersion, setFormVersion] = useState({});
  const [formuleTexte, setFormuleTexte] = useState('');
  const [formuleResultat, setFormuleResultat] = useState(null);
  const [validationOuverte, setValidationOuverte] = useState(null);
  const [validationVersionOuverte, setValidationVersionOuverte] = useState(null);
  const [erreurValidationRubrique, setErreurValidationRubrique] = useState(null);
  const [erreurValidationVersion, setErreurValidationVersion] = useState(null);
  const [saving, setSaving] = useState(false);

  const charger = () => {
    if (!token) return;
    const params = { limite: 20, page };
    if (recherche) params.recherche = recherche;
    if (typeFiltre) params.type = typeFiltre;
    if (familleFiltre) params.famille = familleFiltre;
    if (statutFiltre) params.statut = statutFiltre;
    axios.get(`${API}/paie/rubriques`, { ...h, params }).then((r) => { setItems(r.data?.data?.items || []); setPageInfo({ page: r.data?.data?.page || 1, pages: r.data?.data?.pages || 1, total: r.data?.data?.total || 0 }); }).catch(() => {});
    axios.get(`${API}/paie/rubriques/qualite`, h).then((r) => setQualite(r.data?.data || null)).catch(() => {});
  };
  useEffect(charger, [token, page, recherche, typeFiltre, familleFiltre, statutFiltre]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [recherche, typeFiltre, familleFiltre, statutFiltre]);

  const notifier = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  // RH-PAIE-2D §22 — formulaire vide par défaut pour type/famille (plus de présélection GAIN/PRIME).
  const ouvrirCreation = () => {
    setForm({ code: '', libelle: '', libelle_bulletin: '', type: '', famille: '', ordre_affichage: 100 });
    setModalOuvert(true);
  };
  const creerRubrique = async () => {
    try {
      await axios.post(`${API}/paie/rubriques`, form, h);
      setModalOuvert(false); notifier('ok', 'Rubrique créée en brouillon.'); charger();
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  // RH-PAIE-2D §10 — plus de validation en un clic pour une rubrique.
  // RH-PAIE-2H — erreur affichée dans la modale, jamais dans la bannière globale.
  const confirmerValidationRubrique = async (commentaire) => {
    if (saving) return;
    setSaving(true);
    setErreurValidationRubrique(null);
    try {
      await axios.patch(`${API}/paie/rubriques/${validationOuverte.id}/valider`, { commentaire, confirmationExplicite: true }, h);
      setValidationOuverte(null); notifier('ok', 'Rubrique validée.'); charger();
    } catch (e) { setErreurValidationRubrique(messageErreurValidation(e)); }
    finally { setSaving(false); }
  };

  const ouvrirVersions = async (rubrique) => {
    setRubriqueOuverte(rubrique);
    const r = await axios.get(`${API}/paie/rubriques/${rubrique.id}/versions`, h).catch(() => null);
    setVersions(r?.data?.data || []);
  };

  // RH-PAIE-2D §7 — plus de présélection de source/niveau pour une version de rubrique.
  const ouvrirCreationVersion = () => {
    setFormVersion({ date_debut: '', mode_calcul: '', source: '', niveau_confiance: '', motif: '' });
    setFormuleTexte('{\n  "op": "MULTIPLY",\n  "args": [{"ref": "BASE"}, {"param": "CODE_DU_PARAMETRE"}]\n}');
    setFormuleResultat(null);
    setModalVersion(true);
  };

  const validerFormuleTexte = async () => {
    try {
      const formule = JSON.parse(formuleTexte);
      const r = await axios.post(`${API}/paie/rubriques/valider-formule`, { formule_declarative: formule }, h);
      setFormuleResultat({ ok: true, references: r.data?.data?.references });
    } catch (e) {
      setFormuleResultat({ ok: false, message: e.response?.data?.message || e.message });
    }
  };

  const creerVersion = async () => {
    try {
      const payload = { ...formVersion };
      if (payload.mode_calcul === 'FORMULE') {
        try { payload.formule_declarative = JSON.parse(formuleTexte); }
        catch { notifier('err', 'La formule n\'est pas un JSON valide.'); return; }
      }
      await axios.post(`${API}/paie/rubriques/${rubriqueOuverte.id}/versions`, payload, h);
      setModalVersion(false);
      notifier('ok', 'Version créée en brouillon.');
      ouvrirVersions(rubriqueOuverte);
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };

  const actionVersion = async (versionId, action) => {
    try {
      await axios.patch(`${API}/paie/rubriques/${rubriqueOuverte.id}/versions/${versionId}/${action}`, {}, h);
      notifier('ok', 'Action effectuée.');
      ouvrirVersions(rubriqueOuverte);
    } catch (e) { notifier('err', e.response?.data?.message || 'Erreur'); }
  };
  // RH-PAIE-2H — erreur affichée dans la modale, jamais dans la bannière globale.
  const confirmerValidationVersion = async (commentaire) => {
    if (saving) return;
    setSaving(true);
    setErreurValidationVersion(null);
    try {
      await axios.patch(`${API}/paie/rubriques/${rubriqueOuverte.id}/versions/${validationVersionOuverte.id}/valider`, { commentaire, confirmationExplicite: true }, h);
      setValidationVersionOuverte(null); notifier('ok', 'Version validée.'); ouvrirVersions(rubriqueOuverte);
    } catch (e) { setErreurValidationVersion(messageErreurValidation(e)); }
    finally { setSaving(false); }
  };

  const formValide = form.code && form.libelle && form.libelle_bulletin && form.type && form.famille;

  return (
    <div>
      <p style={{ fontSize: 12.5, color: '#666', marginTop: -6, marginBottom: 16 }}>Une rubrique correspond à une ligne du futur bulletin de salaire.</p>
      {msg && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: msg.type === 'ok' ? '#E8F5E9' : '#FFEBEE', color: msg.type === 'ok' ? VERT : ROUGE, fontWeight: 600, fontSize: 13 }}>{msg.text}</div>}

      {/* RH-PAIE-2D §13 — compteurs cliquables (filtrent par statut) */}
      {qualite && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          {[['Total rubriques', qualite.total_rubriques, ''], ['Brouillons', qualite.brouillons, 'BROUILLON'], ['Validées', qualite.validees, 'VALIDE'], ['Archivées', qualite.archivees, 'ARCHIVE'], ['Sans version active', qualite.rubriques_sans_version_active, '']].map(([label, val, statutCible]) => (
            <button key={label} onClick={() => statutCible !== '' ? setStatutFiltre(statutCible) : setStatutFiltre('')}
              style={{ background: statutFiltre === statutCible && statutCible ? '#E8F5E9' : '#fff', border: `1px solid ${statutFiltre === statutCible && statutCible ? VERT : GRIS_BORD}`, borderRadius: 10, padding: '10px 14px', minWidth: 130, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{val}</div>
            </button>
          ))}
        </div>
      )}

      {/* RH-PAIE-2D §13 — recherche + filtres */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input placeholder="Rechercher par code ou libellé…" value={recherche} onChange={(e) => setRecherche(e.target.value)} style={{ ...inp, flex: '1 1 220px' }} />
        <SelectHumanise value={typeFiltre} onChange={setTypeFiltre} dictionnaire={TYPES_RUBRIQUE_DICT} placeholder="Tous les types" style={{ minWidth: 160, width: 'auto' }} />
        <SelectHumanise value={familleFiltre} onChange={setFamilleFiltre} dictionnaire={FAMILLES_RUBRIQUE_DICT} placeholder="Toutes les familles" style={{ minWidth: 180, width: 'auto' }} />
        <select value={statutFiltre} onChange={(e) => setStatutFiltre(e.target.value)} style={{ ...inp, minWidth: 150 }}>
          <option value="">Tous les statuts</option>
          {Object.keys(STATUTS_PAIE_DICT).map((s) => <option key={s} value={s}>{humaniserStatut(s)}</option>)}
        </select>
        <button onClick={ouvrirCreation} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}>Nouvelle rubrique</button>
      </div>

      <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10 }}>
          <thead><tr>{['Code', 'Libellé', 'Type', 'Famille', 'Statut', ''].map((th) => <th key={th} style={{ textAlign: 'left', padding: '10px 12px', color: '#888', fontSize: 10.5, textTransform: 'uppercase', borderBottom: `2px solid ${GRIS_BORD}` }}>{th}</th>)}</tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 700 }}>{r.code}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{r.libelle}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{humaniserTypeRubrique(r.type)}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>{humaniserFamilleRubrique(r.famille)}</td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}><BadgePaie texte={humaniserStatut(r.statut)} couleur={COULEUR_STATUT_PAIE[r.statut] || '#888'} /></td>
                <td style={{ padding: '9px 12px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {r.statut === 'BROUILLON' && <button onClick={() => { setErreurValidationRubrique(null); setValidationOuverte(r); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: VERT, color: '#fff', cursor: 'pointer' }}>Valider</button>}
                    {r.statut === 'VALIDE' && <button onClick={() => ouvrirVersions(r)} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${VERT}`, background: 'none', color: VERT, cursor: 'pointer' }}>Versions</button>}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#999' }}>Aucune rubrique ne correspond.</td></tr>}
          </tbody>
        </table>
      </div>

      {pageInfo.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: '#fff', fontSize: 12, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>Précédent</button>
          <span style={{ fontSize: 12, color: '#666', alignSelf: 'center' }}>Page {pageInfo.page} / {pageInfo.pages} — {pageInfo.total} rubrique(s)</span>
          <button disabled={page >= pageInfo.pages} onClick={() => setPage((p) => p + 1)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: '#fff', fontSize: 12, cursor: page >= pageInfo.pages ? 'not-allowed' : 'pointer', opacity: page >= pageInfo.pages ? 0.5 : 1 }}>Suivant</button>
        </div>
      )}

      {modalOuvert && (
        <ModalPaie titre="Nouvelle rubrique" onClose={() => setModalOuvert(false)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Code *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Libellé *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Libellé bulletin *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.libelle_bulletin} onChange={(e) => setForm({ ...form, libelle_bulletin: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Type *</label><SelectHumanise value={form.type} onChange={(v) => setForm({ ...form, type: v })} dictionnaire={TYPES_RUBRIQUE_DICT} placeholder="Sélectionner un type" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Famille *</label><SelectHumanise value={form.famille} onChange={(v) => setForm({ ...form, famille: v })} dictionnaire={FAMILLES_RUBRIQUE_DICT} placeholder="Sélectionner une famille" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Ordre d'affichage <span style={{ fontWeight: 400, color: '#999' }}>(suggestion, modifiable)</span></label><input type="number" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.ordre_affichage} onChange={(e) => setForm({ ...form, ordre_affichage: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setModalOuvert(false)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button disabled={!formValide} onClick={creerRubrique} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: formValide ? 'pointer' : 'not-allowed', opacity: formValide ? 1 : 0.5 }}>Créer en brouillon</button>
          </div>
        </ModalPaie>
      )}

      {validationOuverte && (
        <ModaleValidationSecurisee
          titre={`Valider la rubrique — ${validationOuverte.libelle}`}
          saving={saving}
          synthese={[
            ['Code', validationOuverte.code], ['Libellé', validationOuverte.libelle], ['Libellé bulletin', validationOuverte.libelle_bulletin],
            ['Type', humaniserTypeRubrique(validationOuverte.type)], ['Famille', humaniserFamilleRubrique(validationOuverte.famille)],
            ['Ordre d\'affichage', validationOuverte.ordre_affichage], ['Visible sur bulletin', validationOuverte.visible_bulletin ? 'Oui' : 'Non'],
          ]}
          erreur={erreurValidationRubrique}
          onClose={() => setValidationOuverte(null)}
          onConfirmer={confirmerValidationRubrique}
        />
      )}

      {rubriqueOuverte && !modalVersion && !validationVersionOuverte && (
        <ModalPaie titre={`Versions — ${rubriqueOuverte.code}`} onClose={() => setRubriqueOuverte(null)} large>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button onClick={ouvrirCreationVersion} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Nouvelle version</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Date d\'effet', 'Mode', 'Statut', ''].map((th) => <th key={th} style={{ textAlign: 'left', padding: 8, color: '#888', fontSize: 10 }}>{th}</th>)}</tr></thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>{new Date(v.date_debut).toLocaleDateString('fr-FR')}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>{humaniserModeCalcul(v.mode_calcul)}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}><BadgeStatutPaie statut={v.statut} niveauConfiance={v.niveau_confiance} /></td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                    {v.statut === 'BROUILLON' && <button onClick={() => actionVersion(v.id, 'soumettre')} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `1px solid ${VERT}`, background: 'none', color: VERT, cursor: 'pointer' }}>Soumettre</button>}
                    {v.statut === 'A_VALIDER' && <button onClick={() => { setErreurValidationVersion(null); setValidationVersionOuverte(v); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: VERT, color: '#fff', cursor: 'pointer' }}>Valider</button>}
                  </td>
                </tr>
              ))}
              {versions.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 16, color: '#999' }}>Aucune version.</td></tr>}
            </tbody>
          </table>
        </ModalPaie>
      )}

      {validationVersionOuverte && (
        <ModaleValidationSecurisee
          titre="Valider cette version de rubrique"
          saving={saving}
          synthese={[
            ['Date d\'effet', fmtDate2D(validationVersionOuverte.date_debut)], ['Mode de calcul', humaniserModeCalcul(validationVersionOuverte.mode_calcul)],
            ['Source', humaniserSource(validationVersionOuverte.source)], ['Niveau de validation', humaniserNiveauConfiance(validationVersionOuverte.niveau_confiance)],
            ['Motif', validationVersionOuverte.motif],
          ]}
          erreur={erreurValidationVersion}
          onClose={() => setValidationVersionOuverte(null)}
          onConfirmer={confirmerValidationVersion}
        />
      )}

      {modalVersion && (
        <ModalPaie titre="Nouvelle version" onClose={() => setModalVersion(false)} large>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Date d'effet *</label><input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={formVersion.date_debut} onChange={(e) => setFormVersion({ ...formVersion, date_debut: e.target.value })} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Mode de calcul *</label><SelectHumanise value={formVersion.mode_calcul} onChange={(v) => setFormVersion({ ...formVersion, mode_calcul: v })} dictionnaire={MODES_CALCUL_RUBRIQUE_DICT} placeholder="Sélectionner un mode" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Source *</label><SelectHumanise value={formVersion.source} onChange={(v) => setFormVersion({ ...formVersion, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600 }}>Niveau de validation *</label><SelectHumanise value={formVersion.niveau_confiance} onChange={(v) => setFormVersion({ ...formVersion, niveau_confiance: v })} dictionnaire={NIVEAUX_CONFIANCE_PAIE} placeholder="Sélectionner un niveau" /></div>
          </div>
          {formVersion.mode_calcul === 'FORMULE' && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Formule déclarative (JSON — jamais de code exécutable)</label>
              <textarea style={{ ...inp, width: '100%', minHeight: 100, boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }} value={formuleTexte} onChange={(e) => setFormuleTexte(e.target.value)} />
              <button onClick={validerFormuleTexte} style={{ marginTop: 6, fontSize: 12, padding: '5px 12px', borderRadius: 6, border: `1px solid ${VERT}`, background: 'none', color: VERT, cursor: 'pointer' }}>Valider la syntaxe</button>
              {formuleResultat && (
                <div style={{ marginTop: 8, fontSize: 12, color: formuleResultat.ok ? VERT : ROUGE }}>
                  {formuleResultat.ok ? `Formule valide. Références : ${JSON.stringify(formuleResultat.references)}` : formuleResultat.message}
                </div>
              )}
            </div>
          )}
          {formVersion.mode_calcul === 'REFERENCE_MODULE' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Module</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value="IPM" disabled /></div>
              <div><label style={{ fontSize: 12, fontWeight: 600 }}>Clé rh.ipm_parametres.cle *</label><input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={formVersion.reference_module_cle || ''} onChange={(e) => setFormVersion({ ...formVersion, reference_module: 'IPM', reference_module_cle: e.target.value })} /></div>
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Motif *</label>
            <textarea style={{ ...inp, width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} value={formVersion.motif} onChange={(e) => setFormVersion({ ...formVersion, motif: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setModalVersion(false)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
            <button onClick={creerVersion} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: VERT, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Créer en brouillon</button>
          </div>
        </ModalPaie>
      )}
    </div>
  );
};

// Socle IPM (3 octobre 2026) : les paramètres IPM sont des versions datées et sourcées, gérées dans
// le module IPM par les personnes habilitées (brouillon → validation par une autre personne).
// Aucune valeur n'est saisie ici : les anciens taux et forfaits étaient des valeurs d'essai.
const IpmSection = () => {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>IPM</h2>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: '#4A4A4A' }}>
        Les paramètres de l'IPM (taux de prise en charge, âge des enfants, cotisations, limites de retenue)
        se gèrent désormais dans le module IPM, en versions datées et sourcées : chaque valeur cite sa référence
        (règlement intérieur, décision du conseil d'administration, texte officiel) et doit être validée par une
        autre personne habilitée. L'accès demande une habilitation IPM attribuée explicitement au poste.
      </p>
      <button type="button" className="rh-btn rh-btn-primaire" onClick={() => navigate('/ipm?onglet=parametres')}>Ouvrir les paramètres IPM</button>
    </div>
  );
};

// ── RH-PAIE-2F §5 — Référentiels à compléter : lecture seule, informatif,
// ne modifie jamais automatiquement une donnée historique. ─────────────────
const OrphelinsSection = () => {
  const [orphelins, setOrphelins] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const res = await axios.get(`${API}/referentiel/orphelins`);
      setOrphelins(res.data?.data || res.data);
    } catch (e) { setErreur(e.response?.data?.message || 'Erreur de chargement'); }
  };

  const Puce = ({ children }) => (
    <span style={{ fontSize: 11, color: ORANGE_2D, background: '#FFF3E0', padding: '2px 8px', borderRadius: 12, marginRight: 6 }}>{children}</span>
  );

  const Liste = ({ titre, items, vide }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>{titre}</div>
      {(!items || items.length === 0) ? (
        <div style={{ fontSize: 12.5, color: '#888' }}>{vide}</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((it, i) => <Puce key={i}>{it.nom || it.intitule || String(it)}</Puce>)}
        </div>
      )}
    </div>
  );

  if (erreur) return <Card titre="Référentiels à compléter" Icone={AlertTriangle}><div style={{ color: ROUGE, fontSize: 13 }}>{erreur}</div></Card>;
  if (!orphelins) return <Card titre="Référentiels à compléter" Icone={AlertTriangle}><div style={{ fontSize: 13, color: '#888' }}>Chargement…</div></Card>;

  return (
    <Card titre="Référentiels à compléter" Icone={AlertTriangle}>
      <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 20 }}>
        Ces éléments fonctionnent normalement (aucune saisie n'est bloquée) mais ne sont pas encore reliés au niveau organisationnel supérieur. Aucune donnée n'est modifiée automatiquement ici — le rattachement se fait depuis les écrans Départements / Services / Postes.
      </p>
      <Liste titre="Départements sans site" items={orphelins.departements_sans_site} vide="Tous les départements sont rattachés à un site." />
      <Liste titre="Services sans département" items={orphelins.services_sans_departement} vide="Tous les services sont rattachés à un département." />
      <Liste titre="Postes sans rattachement organisationnel" items={orphelins.postes_sans_rattachement} vide="Tous les postes sont rattachés." />
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${GRIS_BORD}` }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: ORANGE_2D }}>{orphelins.employes_departement_non_reference ?? 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Employé(s) — département non référencé</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: ORANGE_2D }}>{orphelins.employes_service_non_reference ?? 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Employé(s) — service non référencé</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: ORANGE_2D }}>{orphelins.employes_poste_libre ?? 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Employé(s) — poste en saisie libre (non référencé)</div>
        </div>
      </div>
    </Card>
  );
};

// ── RH-PAIE-2F §6 — Classification manuelle des catégories A_CLASSER.
// Strictement additive : jamais de déduction automatique, jamais de modification
// d'un employé/dossier/grille existant, toujours un aperçu avant confirmation. ──
const ClassificationCategoriesSection = () => {
  const [aClasser, setAClasser] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [formOuvert, setFormOuvert] = useState(false);
  const [form, setForm] = useState({
    code: '', famille_professionnelle: '', libelle: '', sans_echelon: true, echelons: '',
    date_effet: '', source: '', reference: '', motif: '', commentaire: '',
  });
  const [apercu, setApercu] = useState(null);
  const [localMsg, setLocalMsg] = useState(null);
  const [chargementApercu, setChargementApercu] = useState(false);
  const [confirmPending, setConfirmPending] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/paie/categories-salariales/a-classer`),
        axios.get(`${API}/paie/categories-salariales/classification/historique`),
      ]);
      setAClasser(r1.data?.data || []);
      setHistorique(r2.data?.data || []);
    } catch (e) { console.error(e); }
  };

  const showMsg = (texte, type = 'ok') => { setLocalMsg({ texte, type }); setTimeout(() => setLocalMsg(null), 4000); };

  const ouvrirPour = (code) => {
    setForm({ code, famille_professionnelle: '', libelle: '', sans_echelon: true, echelons: '', date_effet: '', source: '', reference: '', motif: '', commentaire: '' });
    setApercu(null);
    setFormOuvert(true);
  };

  const demanderApercu = async () => {
    if (!form.code || !form.famille_professionnelle) return showMsg('Code et famille professionnelle sont requis.', 'err');
    setChargementApercu(true);
    try {
      const res = await axios.get(`${API}/paie/categories-salariales/classification/apercu`, { params: { code: form.code, famille_professionnelle: form.famille_professionnelle } });
      setApercu(res.data?.data);
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur lors du calcul de l\'aperçu', 'err'); }
    finally { setChargementApercu(false); }
  };

  const confirmerClassification = () => {
    if (!form.libelle || !form.date_effet || !form.source || !form.motif) {
      return showMsg('Libellé, date d\'effet, source et motif sont requis.', 'err');
    }
    setConfirmPending({
      titre: 'Confirmer la classification ?',
      message: `Créer la référence "${form.famille_professionnelle} — ${form.code}" (${form.libelle}). Cette action n'attribue automatiquement cette référence à aucun employé, dossier salarial ou grille existant.`,
      action: 'Classer',
      onConfirm: async () => {
        try {
          await axios.post(`${API}/paie/categories-salariales/classification`, {
            code: form.code,
            famille_professionnelle: form.famille_professionnelle,
            libelle: form.libelle,
            echelons_disponibles: form.sans_echelon ? [] : form.echelons.split(',').map(s => s.trim()).filter(Boolean),
            date_effet: form.date_effet,
            source: form.source,
            reference: form.reference,
            motif: form.motif,
            commentaire: form.commentaire,
          });
          showMsg('Catégorie classée. Les employés concernés devront être rattachés séparément, un par un.');
          setFormOuvert(false);
          setApercu(null);
          // RH-PAIE-2G — sans ceci, un CategorieSelector déjà monté ailleurs dans la session
          // (dossier salarial, grille salariale) continuerait d'afficher la liste chargée avant
          // cette classification, laissant croire à tort que la catégorie est toujours "à classer".
          invaliderCacheCategories();
          charger();
        } catch (e) { showMsg(e.response?.data?.message || 'Erreur lors de la classification', 'err'); }
      },
    });
  };

  return (
    <>
      <Card titre="Catégories historiques à classer" Icone={Tag}>
        <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 16 }}>
          Ces codes existent dans l'historique mais n'ont jamais été rattachés à une famille professionnelle (Ouvriers / Employés / Agents de maîtrise / Cadres). Aucun classement automatique n'est possible : une décision explicite de la DRH est requise pour chaque code. Un même code peut être classé dans plusieurs familles (ce sont alors des références distinctes).
        </p>
        {localMsg && (
          <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 8, fontSize: 13, background: localMsg.type === 'err' ? '#FDECEA' : '#E8F5E9', color: localMsg.type === 'err' ? ROUGE : VERT }}>
            {localMsg.texte}
          </div>
        )}
        {aClasser.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888' }}>Aucun code encore A_CLASSER, ou tous ont déjà au moins une référence classée.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {aClasser.map(c => (
              <div key={c.code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: '#F8F9FA', border: `1px solid ${GRIS_BORD}`, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>Catégorie {c.code}</span>
                  <span style={{ marginLeft: 8, fontSize: 11, color: ORANGE_2D, background: '#FFF3E0', padding: '2px 8px', borderRadius: 12 }}>À classer</span>
                  <span style={{ marginLeft: 10, fontSize: 12, color: '#666' }}>{c.employes_concernes} employé(s) historique(s) · {c.dossiers_concernes} dossier(s) salarial(aux)</span>
                </div>
                <button onClick={() => ouvrirPour(c.code)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12.5 }}>
                  Classer cette catégorie
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {formOuvert && (
        <Card titre={`Classification — Catégorie ${form.code}`} Icone={Tag}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Famille professionnelle *</label>
              <SelectHumanise value={form.famille_professionnelle} onChange={(v) => { setForm({ ...form, famille_professionnelle: v }); setApercu(null); }} dictionnaire={FAMILLES_PROFESSIONNELLES} placeholder="Sélectionner une famille" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Libellé humain *</label>
              <input value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} placeholder="Ex. : Employé de bureau" style={{ ...inp, width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Échelons applicables</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Toggle on={!form.sans_echelon} onClick={() => setForm({ ...form, sans_echelon: !form.sans_echelon })} />
                <span style={{ fontSize: 12.5 }}>{form.sans_echelon ? 'Sans échelon' : 'Avec échelons'}</span>
              </div>
              {!form.sans_echelon && (
                <input value={form.echelons} onChange={e => setForm({ ...form, echelons: e.target.value })} placeholder="Ex. : A,B,C (séparés par des virgules)" style={{ ...inp, width: '100%', marginTop: 6 }} />
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Date d'effet *</label>
              <input type="date" value={form.date_effet} onChange={e => setForm({ ...form, date_effet: e.target.value })} style={{ ...inp, width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Source *</label>
              <SelectHumanise value={form.source} onChange={(v) => setForm({ ...form, source: v })} dictionnaire={SOURCES_PAIE_DICT} placeholder="Sélectionner une source" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Référence</label>
              <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Ex. : numéro de note, article…" style={{ ...inp, width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Motif *</label>
              <input value={form.motif} onChange={e => setForm({ ...form, motif: e.target.value })} placeholder="Pourquoi ce classement ?" style={{ ...inp, width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Commentaire</label>
              <input value={form.commentaire} onChange={e => setForm({ ...form, commentaire: e.target.value })} style={{ ...inp, width: '100%' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button onClick={demanderApercu} disabled={chargementApercu} style={{ background: '#fff', color: VERT, border: `1px solid ${VERT}`, borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              {chargementApercu ? 'Calcul…' : 'Voir l\'aperçu avant confirmation'}
            </button>
            <button onClick={() => { setFormOuvert(false); setApercu(null); }} style={{ background: 'none', border: `1px solid ${GRIS_BORD}`, color: '#666', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
              Annuler
            </button>
          </div>

          {apercu && (
            <div style={{ background: '#F8F9FA', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 10 }}>Aperçu — aucune modification n'a encore été appliquée</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
                <div><div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.employes_concernes ?? 0}</div><div style={{ fontSize: 12, color: '#666' }}>Employé(s) sur ce code historique</div></div>
                <div><div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.dossiers_concernes ?? 0}</div><div style={{ fontSize: 12, color: '#666' }}>Dossier(s) salarial(aux) concerné(s)</div></div>
                <div><div style={{ fontSize: 20, fontWeight: 700 }}>{apercu.grilles_concernees ?? 0}</div><div style={{ fontSize: 12, color: '#666' }}>Ligne(s) de grille concernée(s)</div></div>
              </div>
              <div style={{ fontSize: 12.5, color: '#666', marginBottom: 6 }}>Aucun de ces employés, dossiers ou grilles ne sera modifié par cette classification. Le rattachement se fera ensuite, individuellement, par un utilisateur autorisé.</div>
              {apercu.conflit ? (
                <div style={{ color: ROUGE, fontSize: 13, fontWeight: 600 }}>Une référence "{form.famille_professionnelle} — {form.code}" existe déjà : cette création est refusée.</div>
              ) : (
                <div style={{ color: VERT, fontSize: 13 }}>Aucun conflit : cette combinaison famille + code n'existe pas encore.</div>
              )}
              {apercu.references_existantes?.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12.5 }}>
                  <div style={{ color: '#666', marginBottom: 4 }}>Ce code existe déjà dans d'autres familles :</div>
                  {apercu.references_existantes.map((r, i) => (
                    <span key={i} style={{ marginRight: 6, fontSize: 11, background: '#E3F2FD', color: '#1565C0', padding: '2px 8px', borderRadius: 12 }}>{humaniserFamilleProfessionnelle(r.famille_professionnelle)} — {r.code}</span>
                  ))}
                </div>
              )}
              {!apercu.conflit && (
                <button onClick={confirmerClassification} style={{ marginTop: 14, background: VERT, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13.5 }}>
                  Confirmer la classification
                </button>
              )}
            </div>
          )}
        </Card>
      )}

      <Card titre="Historique des classifications" Icone={Clock}>
        {historique.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888' }}>Aucune classification effectuée pour l'instant.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: `1px solid ${GRIS_BORD}` }}>
                  <th style={{ padding: '6px 8px' }}>Date</th>
                  <th style={{ padding: '6px 8px' }}>Famille</th>
                  <th style={{ padding: '6px 8px' }}>Code</th>
                  <th style={{ padding: '6px 8px' }}>Libellé</th>
                  <th style={{ padding: '6px 8px' }}>Auteur</th>
                  <th style={{ padding: '6px 8px' }}>Motif</th>
                </tr>
              </thead>
              <tbody>
                {historique.map((h, i) => {
                  const v = h.nouvelles_valeurs || {};
                  const auteur = [h.prenom, h.nom].filter(Boolean).join(' ');
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '6px 8px' }}>{h.created_at ? new Date(h.created_at).toLocaleDateString('fr-FR') : '–'}</td>
                      <td style={{ padding: '6px 8px' }}>{humaniserFamilleProfessionnelle(v.famille_professionnelle)}</td>
                      <td style={{ padding: '6px 8px' }}>{v.code}</td>
                      <td style={{ padding: '6px 8px' }}>{v.libelle}</td>
                      <td style={{ padding: '6px 8px' }}>{auteur || '–'}</td>
                      <td style={{ padding: '6px 8px' }}>{v.motif || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {confirmPending && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{background:'#fff',borderRadius:14,padding:'24px 28px',maxWidth:420,width:'calc(100% - 32px)',boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}><div style={{fontSize:16,fontWeight:700,color:'#1A1A1A',marginBottom:8}}>{confirmPending.titre}</div><div style={{fontSize:13,color:'#666',marginBottom:24}}>{confirmPending.message}</div><div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button onClick={()=>setConfirmPending(null)} style={{padding:'10px 18px',border:'1px solid #e0e0e0',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>Annuler</button><button onClick={()=>{confirmPending.onConfirm();setConfirmPending(null);}} style={{padding:'10px 18px',border:'none',borderRadius:8,background:VERT,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>{confirmPending.action}</button></div></div></div>)}
    </>
  );
};

// RH-PAIE-2I §12 — Centre de validation transversal. Agrège en LECTURE SEULE ce qui est déjà
// "à valider" dans les quatre domaines (grille, paramètres, rubriques, dossiers salariaux —
// ces derniers couvrant structurellement augmentations/promotions/corrections administratives,
// qui sont toutes des versions de dossier salarial distinguées par leur `source`). N'introduit
// aucune seconde voie d'action : chaque ligne renvoie vers l'écran déjà existant où l'action
// (valider/rejeter/annuler) est réellement exécutée — jamais une logique de validation
// dupliquée ou divergente de celle du moteur central (§6).
const DOMAINE_LABELS = {
  GRILLE_SALARIALE: 'Grille salariale',
  PARAMETRE_PAIE: 'Paramètre de paie',
  RUBRIQUE_PAIE: 'Rubrique de paie',
  DOSSIER_SALARIAL: 'Dossier salarial',
};
const DOMAINE_COULEURS = {
  GRILLE_SALARIALE: '#1B5E20',
  PARAMETRE_PAIE: '#1565C0',
  RUBRIQUE_PAIE: '#6A1B9A',
  DOSSIER_SALARIAL: '#E65100',
};
const CentreValidationSection = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filtreDomaine, setFiltreDomaine] = useState('');
  const [erreur, setErreur] = useState(null);

  const charger = () => {
    axios.get(`${API}/paie/centre-validation`)
      .then((r) => setData(r.data?.data))
      .catch((e) => setErreur(e.response?.data?.message || 'Erreur de chargement'));
  };
  useEffect(charger, []);

  const ouvrir = (item) => {
    if (item.domaine === 'GRILLE_SALARIALE') { navigate('/parametres'); setTimeout(() => window.scrollTo(0, 0), 100); }
    else if (item.domaine === 'PARAMETRE_PAIE') navigate('/parametres');
    else if (item.domaine === 'RUBRIQUE_PAIE') navigate('/parametres');
    else if (item.domaine === 'DOSSIER_SALARIAL' && item.employe?.id) navigate(`/employes/${item.employe.id}?onglet=paie`);
  };

  if (erreur) return <Card titre="Centre de validation" Icone={CheckCircle}><div style={{ color: ROUGE, fontSize: 13 }}>{erreur}</div></Card>;
  if (!data) return <Card titre="Centre de validation" Icone={CheckCircle}><div style={{ fontSize: 13, color: '#888' }}>Chargement…</div></Card>;

  const items = filtreDomaine ? data.items.filter((i) => i.domaine === filtreDomaine) : data.items;

  return (
    <Card titre="Centre de validation" Icone={CheckCircle}>
      <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 16 }}>
        Tout ce qui attend une décision, tous domaines de paie confondus — grille salariale, paramètres de paie, rubriques, et versions
        de dossier salarial (dont augmentations, promotions et corrections administratives). Chaque ligne renvoie vers l'écran où
        l'action réelle (valider, rejeter, annuler) s'effectue — ce centre n'exécute aucune action lui-même.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={() => setFiltreDomaine('')} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${GRIS_BORD}`, background: !filtreDomaine ? VERT : '#fff', color: !filtreDomaine ? '#fff' : '#555', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Tout ({data.total})
        </button>
        {Object.entries(data.par_domaine).map(([dom, n]) => (
          <button key={dom} onClick={() => setFiltreDomaine(dom)} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${DOMAINE_COULEURS[dom]}55`, background: filtreDomaine === dom ? DOMAINE_COULEURS[dom] : '#fff', color: filtreDomaine === dom ? '#fff' : DOMAINE_COULEURS[dom], fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {DOMAINE_LABELS[dom]} ({n})
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888' }}>Rien n'est actuellement en attente de validation.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item) => (
            <div key={`${item.domaine}-${item.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '10px 14px', borderRadius: 8, background: '#F8F9FA', border: `1px solid ${GRIS_BORD}` }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: DOMAINE_COULEURS[item.domaine], padding: '2px 8px', borderRadius: 10 }}>{DOMAINE_LABELS[item.domaine]}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>{item.type_demande}</span>
                  {item.employe && <span style={{ fontSize: 12, color: '#666' }}>{item.employe.prenom} {item.employe.nom} ({item.employe.matricule})</span>}
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>{item.libelle}</div>
                <div style={{ fontSize: 11.5, color: '#888', marginTop: 2 }}>
                  {item.valeur_apres && <>→ {item.valeur_apres} · </>}
                  {item.date_effet && <>Effet {fmtDate2D(item.date_effet)} · </>}
                  {item.auteur && <>Auteur : {item.auteur} · </>}
                  {item.motif && <>Motif : {item.motif}</>}
                </div>
                <LigneSuivi suivi={item.suivi} />
              </div>
              <button onClick={() => ouvrir(item)} style={{ background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                Ouvrir
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ── Composant principal ────────────────────────────────────────────────────

export default function ParametresPage() {
  const [sites, setSites]           = useState([]);
  const [departements, setDepartements] = useState([]);
  const [services, setServices]     = useState([]);
  const [usages, setUsages]         = useState({ departements: {}, services: {}, sites: {} });
  const [orphelins, setOrphelins]   = useState(null);
  const [editItem, setEditItem]     = useState(null);
  // Centre de configuration (Lot 1) : ouverture directe d'une section depuis une tuile (« ?section=… »).
  const [parametresUrl] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    const demandee = parametresUrl.get('section');
    return demandee && SECTIONS.some((x) => x.id === demandee) ? demandee : 'sites';
  });
  const [msg, setMsg]               = useState(null);
  const [confirmPending, setConfirmPending] = useState(null);

  const refSiteCode = useRef(null);
  const refSiteNom  = useRef(null);
  const refDep      = useRef(null);
  const refDepSite  = useRef(null);
  const refSrv      = useRef(null);
  const refSrvDep   = useRef(null);

  useEffect(() => { chargerTout(); }, []);

  const chargerTout = async () => {
    try {
      const [s, d, sv, u, o] = await Promise.all([
        axios.get('/api/referentiel/sites'),
        axios.get('/api/referentiel/departements'),
        axios.get('/api/referentiel/services'),
        axios.get('/api/referentiel/usages'),
        axios.get('/api/referentiel/orphelins'),
      ]);
      setSites(Array.isArray(s.data.sites) ? s.data.sites : []);
      setDepartements(Array.isArray(d.data.departements) ? d.data.departements : []);
      setServices(Array.isArray(sv.data.services) ? sv.data.services : []);
      setUsages(u.data || { departements: {}, services: {}, sites: {} });
      setOrphelins(o.data || null);
    } catch (e) { console.error(e); }
  };

  const showMsg = (texte, type = 'ok') => {
    setMsg({ texte, type });
    setTimeout(() => setMsg(null), 3000);
  };

  // ── SITES ────────────────────────────────────────────────
  const ajouterSite = async () => {
    const code = refSiteCode.current?.value?.trim();
    const nom  = refSiteNom.current?.value?.trim();
    if (!code || !nom) return showMsg('Code et nom requis', 'err');
    try {
      await axios.post('/api/referentiel/sites', { code, nom });
      refSiteCode.current.value = '';
      refSiteNom.current.value  = '';
      chargerTout();
      showMsg('Site ajouté');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const sauvegarderSite = async () => {
    const code = editItem.code?.trim();
    const nom  = editItem.nom?.trim();
    if (!code || !nom) return showMsg('Code et nom requis', 'err');
    try {
      await axios.put(`/api/referentiel/sites/${editItem.id}`, { code, nom });
      setEditItem(null);
      chargerTout();
      showMsg('Site modifié');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  // Module 0 — suppression refusée par le serveur (409) dès que l'entité est utilisée : on
  // propose alors l'archivage, jamais une suppression en cascade ni une mise à NULL.
  const changerArchivage = async (chemin, id, actif, libelle) => {
    try {
      await axios.patch(`/api/referentiel/${chemin}/${id}/${actif ? 'reactiver' : 'archiver'}`);
      chargerTout();
      showMsg(`${libelle} ${actif ? 'réactivé' : 'archivé'}`);
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };
  const supprimerOuArchiver = (chemin, id, libelle) => {
    setConfirmPending({ titre: `Supprimer ce ${libelle.toLowerCase()} ?`, message: 'La suppression est refusée s\'il est déjà utilisé : l\'archivage vous sera alors proposé.', action: 'Supprimer', onConfirm: async () => {
      try { await axios.delete(`/api/referentiel/${chemin}/${id}`); chargerTout(); showMsg(`${libelle} supprimé`); }
      catch (e) {
        if (e.response?.status === 409 && e.response?.data?.archivage_possible) {
          setConfirmPending({ titre: `Archiver ce ${libelle.toLowerCase()} ?`, message: `${e.response.data.message} Il restera visible dans l'historique mais ne sera plus proposé.`, action: 'Archiver', onConfirm: () => changerArchivage(chemin, id, false, libelle) });
        } else showMsg(e.response?.data?.message || 'Erreur', 'err');
      }
    }});
  };

  const supprimerSite = (id) => supprimerOuArchiver('sites', id, 'Site');

  // ── DÉPARTEMENTS ─────────────────────────────────────────
  const ajouterDep = async () => {
    const nom = refDep.current?.value?.trim();
    const site_id = refDepSite.current?.value || null;
    if (!nom) return showMsg('Nom requis', 'err');
    try {
      await axios.post('/api/referentiel/departements', { nom, site_id });
      refDep.current.value = '';
      if (refDepSite.current) refDepSite.current.value = '';
      chargerTout();
      showMsg('Département ajouté');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const sauvegarderDep = async () => {
    const nom = editItem.nom?.trim();
    if (!nom) return showMsg('Nom requis', 'err');
    try {
      await axios.put(`/api/referentiel/departements/${editItem.id}`, { nom, site_id: editItem.site_id || null });
      setEditItem(null);
      chargerTout();
      showMsg('Département modifié');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const supprimerDep = (id) => supprimerOuArchiver('departements', id, 'Département');

  // ── SERVICES ─────────────────────────────────────────────
  const ajouterSrv = async () => {
    const nom = refSrv.current?.value?.trim();
    const departement_id = refSrvDep.current?.value || null;
    if (!nom) return showMsg('Nom requis', 'err');
    try {
      await axios.post('/api/referentiel/services', { nom, departement_id });
      refSrv.current.value = '';
      if (refSrvDep.current) refSrvDep.current.value = '';
      chargerTout();
      showMsg('Service ajouté');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const sauvegarderSrv = async () => {
    const nom = editItem.nom?.trim();
    if (!nom) return showMsg('Nom requis', 'err');
    try {
      await axios.put(`/api/referentiel/services/${editItem.id}`, { nom, departement_id: editItem.departement_id || null });
      setEditItem(null);
      chargerTout();
      showMsg('Service modifié');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const supprimerSrv = (id) => supprimerOuArchiver('services', id, 'Service');

  const activeLabel = SECTIONS.find(s => s.id === activeSection)?.label || 'Paramètres';

  return (
    <Layout>
      {/* Toast notification — position fixed, indépendant du layout */}
      {msg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: msg.type === 'err' ? '#FFEBEE' : '#E8F5E9',
          color: msg.type === 'err' ? ROUGE : VERT,
          border: `1px solid ${msg.type === 'err' ? '#FFCDD2' : '#C8E6C9'}`,
          borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center',
        }}>
          {msg.type === 'err'
            ? <XCircle size={14} style={{ marginRight: 6, flexShrink: 0 }} />
            : <CheckCircle size={14} style={{ marginRight: 6, flexShrink: 0 }} />}
          {msg.texte}
        </div>
      )}

      {confirmPending && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{background:'#fff',borderRadius:14,padding:'24px 28px',maxWidth:380,width:'calc(100% - 32px)',boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}><div style={{fontSize:16,fontWeight:700,color:'#1A1A1A',marginBottom:8}}>{confirmPending.titre}</div><div style={{fontSize:13,color:'#666',marginBottom:24}}>{confirmPending.message}</div><div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button onClick={()=>setConfirmPending(null)} style={{padding:'10px 18px',border:'1px solid #e0e0e0',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>Annuler</button><button onClick={()=>{confirmPending.onConfirm();setConfirmPending(null);}} style={{padding:'10px 18px',border:'none',borderRadius:8,background:ROUGE,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>{confirmPending.action}</button></div></div></div>)}

      <style>{`
        @media (max-width: 767px) {
          .params-shell { flex-direction: column; }
          .params-nav {
            width: 100% !important; flex-shrink: 0;
            display: flex; overflow-x: auto; white-space: nowrap;
            border-right: none; border-bottom: 0.5px solid ${GRIS_BORD};
            padding: 10px 10px !important;
          }
          .params-nav-group { display: none; }
          .params-nav button { flex-shrink: 0; width: auto !important; }
          .params-content { padding: 16px 14px 32px !important; }
        }
      `}</style>
      <div className="params-shell" style={{
        display: 'flex', background: '#f7f7f7',
        fontFamily: 'Inter, Segoe UI, sans-serif', minHeight: '100vh',
      }}>

        {/* ── SUBNAV ──────────────────────────────────────── */}
        <nav className="params-nav" style={{
          width: 260, flexShrink: 0,
          background: '#fafafa', borderRight: `0.5px solid ${GRIS_BORD}`,
          padding: '20px 14px',
        }}>
          <div className="params-nav-group" style={{
            fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
            color: '#64748b', margin: '4px 8px 10px', fontWeight: 600,
          }}>
            Configuration
          </div>
          <Link to="/parametres" style={{ display: 'block', margin: '0 8px 8px', fontSize: 13 }}>← Paramètres (tous les domaines)</Link>

          {SECTIONS.map((s, i) => s.group ? (
            <div key={i} className="params-nav-group" style={{
              fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: '#94a3b8', padding: '14px 10px 6px', fontWeight: 500,
            }}>
              {s.group}
            </div>
          ) : (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13.5,
                background: activeSection === s.id ? '#E8F5E9' : 'transparent',
                color: activeSection === s.id ? VERT : '#334155',
                fontWeight: activeSection === s.id ? 500 : 400,
                transition: 'background 120ms ease',
                fontFamily: 'inherit',
              }}
            >
              <s.Icone size={15} />
              {s.label}
            </button>
          ))}
        </nav>

        {/* ── CONTENT ─────────────────────────────────────── */}
        <div className="params-content" style={{ flex: 1, padding: '24px 28px 48px', minWidth: 0 }}>

          {/* Breadcrumb / page header */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', color: '#0f172a' }}>
              {activeLabel}
            </h1>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              Paramètres / {activeLabel}
            </div>
          </div>

          {/* ══ SITES ══ */}
          {activeSection === 'sites' && (
            <Card titre="Sites" Icone={Factory}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input placeholder="Code (ex: DAK)" ref={refSiteCode} defaultValue=""
                  style={{ ...inp, flex: 1 }} />
                <input placeholder="Nom du site" ref={refSiteNom} defaultValue=""
                  style={{ ...inp, flex: 2 }} />
                <button onClick={ajouterSite} style={{
                  background: VERT, color: '#fff', border: 'none', borderRadius: 6,
                  padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}>
                  + Ajouter
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sites.map(s => {
                  const enEdition = editItem?.type === 'site' && editItem?.id === s.id;
                  return (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      background: enEdition ? '#F1F8E9' : '#F8F9FA',
                      border: `1px solid ${enEdition ? '#AED581' : GRIS_BORD}`,
                    }}>
                      {enEdition ? (
                        <>
                          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                            <input value={editItem.code}
                              onChange={e => setEditItem({ ...editItem, code: e.target.value })}
                              style={{ ...inp, width: 90, fontWeight: 700 }} />
                            <input value={editItem.nom}
                              onChange={e => setEditItem({ ...editItem, nom: e.target.value })}
                              style={{ ...inp, flex: 1 }} />
                          </div>
                          <div style={{ display: 'flex', marginLeft: 12 }}>
                            <BtnSave onClick={sauvegarderSite} />
                            <BtnCancel onClick={() => setEditItem(null)} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <span style={{ fontWeight: 700, fontSize: 13, color: VERT, marginRight: 10 }}>{s.code}</span>
                            <span style={{ fontSize: 13, marginRight: 8 }}>{s.nom}</span>
                            <BadgeArchive actif={s.actif} />
                            {(usages.sites?.[s.id] || 0) > 0 && <span style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>{usages.sites[s.id]} employé(s)</span>}
                          </div>
                          <div>
                            <BtnArchive actif={s.actif} onArchiver={() => changerArchivage('sites', s.id, false, 'Site')} onReactiver={() => changerArchivage('sites', s.id, true, 'Site')} />
                            <BtnEdit onClick={() => setEditItem({ type: 'site', id: s.id, code: s.code, nom: s.nom })} />
                            <BtnDel onClick={() => supprimerSite(s.id)} />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* ══ DÉPARTEMENTS ══ */}
          {activeSection === 'departements' && (
            <Card titre="Départements" Icone={Building2}>
              <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 14 }}>
                Un département rattaché à un site limite les employés/services proposés à ce site lors d'une affectation. Laisser « Aucun site » pour un département disponible partout (donnée historique à compléter progressivement).
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <input placeholder="Nom du département" ref={refDep} defaultValue=""
                  style={{ ...inp, flex: 2, minWidth: 160 }} />
                <select ref={refDepSite} defaultValue="" style={{ ...inp, flex: 1, minWidth: 140 }}>
                  <option value="">Aucun site</option>
                  {sites.filter((s) => s.actif !== false).map((s) => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ${s.nom}` : s.nom}</option>)}
                </select>
                <button onClick={ajouterDep} style={{
                  background: VERT, color: '#fff', border: 'none', borderRadius: 6,
                  padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}>
                  + Ajouter
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {departements.map(d => {
                  const enEdition = editItem?.type === 'dep' && editItem?.id === d.id;
                  const nbEmployes = usages.departements?.[d.id] || 0;
                  return (
                    <div key={d.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      background: enEdition ? '#F1F8E9' : '#F8F9FA',
                      border: `1px solid ${enEdition ? '#AED581' : GRIS_BORD}`,
                      flexWrap: 'wrap', gap: 8,
                    }}>
                      {enEdition ? (
                        <>
                          <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 260 }}>
                            <input value={editItem.nom}
                              onChange={e => setEditItem({ ...editItem, nom: e.target.value })}
                              style={{ ...inp, flex: 1 }} />
                            <select value={editItem.site_id || ''} onChange={e => setEditItem({ ...editItem, site_id: e.target.value })} style={{ ...inp, flex: 1 }}>
                              <option value="">Aucun site</option>
                              {sites.filter((s) => s.actif !== false || s.id === editItem.site_id).map((s) => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ${s.nom}` : s.nom}</option>)}
                            </select>
                          </div>
                          <div style={{ display: 'flex', marginLeft: 12 }}>
                            <BtnSave onClick={sauvegarderDep} />
                            <BtnCancel onClick={() => setEditItem(null)} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{d.nom}</span>
                            {d.site_id ? (
                              <span style={{ fontSize: 11, color: VERT, background: '#E8F5E9', padding: '2px 8px', borderRadius: 12 }}>{sites.find(s => s.id === d.site_id)?.nom || 'Site inconnu'}</span>
                            ) : (
                              <span style={{ fontSize: 11, color: ORANGE_2D, background: '#FFF3E0', padding: '2px 8px', borderRadius: 12 }}>À rattacher</span>
                            )}
                            <BadgeArchive actif={d.actif} />
                            {nbEmployes > 0 && <span style={{ fontSize: 11, color: '#666' }}>{nbEmployes} employé(s)</span>}
                          </div>
                          <div>
                            <BtnArchive actif={d.actif} onArchiver={() => changerArchivage('departements', d.id, false, 'Département')} onReactiver={() => changerArchivage('departements', d.id, true, 'Département')} />
                            <BtnEdit onClick={() => setEditItem({ type: 'dep', id: d.id, nom: d.nom, site_id: d.site_id || '' })} />
                            <BtnDel onClick={() => supprimerDep(d.id)} />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* ══ SERVICES ══ */}
          {activeSection === 'services' && (
            <Card titre="Services" Icone={Users}>
              <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 14 }}>
                Un service rattaché à un département limite les postes/employés proposés à ce département lors d'une affectation. Laisser « Aucun département » pour un service disponible partout (donnée historique à compléter progressivement).
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <input placeholder="Nom du service" ref={refSrv} defaultValue=""
                  style={{ ...inp, flex: 2, minWidth: 160 }} />
                <select ref={refSrvDep} defaultValue="" style={{ ...inp, flex: 1, minWidth: 140 }}>
                  <option value="">Aucun département</option>
                  {departements.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                </select>
                <button onClick={ajouterSrv} style={{
                  background: VERT, color: '#fff', border: 'none', borderRadius: 6,
                  padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}>
                  + Ajouter
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {services.map(s => {
                  const enEdition = editItem?.type === 'srv' && editItem?.id === s.id;
                  const depRattache = departements.find(d => d.id === s.departement_id);
                  const nbEmployes = usages.services?.[s.id] || 0;
                  return (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      background: enEdition ? '#F1F8E9' : '#F8F9FA',
                      border: `1px solid ${enEdition ? '#AED581' : GRIS_BORD}`,
                      flexWrap: 'wrap', gap: 8,
                    }}>
                      {enEdition ? (
                        <>
                          <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 260 }}>
                            <input value={editItem.nom}
                              onChange={e => setEditItem({ ...editItem, nom: e.target.value })}
                              style={{ ...inp, flex: 1 }} />
                            <select value={editItem.departement_id || ''} onChange={e => setEditItem({ ...editItem, departement_id: e.target.value })} style={{ ...inp, flex: 1 }}>
                              <option value="">Aucun département</option>
                              {departements.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                            </select>
                          </div>
                          <div style={{ display: 'flex', marginLeft: 12 }}>
                            <BtnSave onClick={sauvegarderSrv} />
                            <BtnCancel onClick={() => setEditItem(null)} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{s.nom}</span>
                            {depRattache ? (
                              <span style={{ fontSize: 11, color: VERT, background: '#E8F5E9', padding: '2px 8px', borderRadius: 12 }}>{depRattache.nom}</span>
                            ) : (
                              <span style={{ fontSize: 11, color: ORANGE_2D, background: '#FFF3E0', padding: '2px 8px', borderRadius: 12 }}>À rattacher</span>
                            )}
                            <BadgeArchive actif={s.actif} />
                            {nbEmployes > 0 && <span style={{ fontSize: 11, color: '#666' }}>{nbEmployes} employé(s)</span>}
                          </div>
                          <div>
                            <BtnArchive actif={s.actif} onArchiver={() => changerArchivage('services', s.id, false, 'Service')} onReactiver={() => changerArchivage('services', s.id, true, 'Service')} />
                            <BtnEdit onClick={() => setEditItem({ type: 'srv', id: s.id, nom: s.nom, departement_id: s.departement_id || '' })} />
                            <BtnDel onClick={() => supprimerSrv(s.id)} />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* ══ NOUVELLES SECTIONS — placeholders ══ */}
          {activeSection === 'referentiels-a-completer' && <OrphelinsSection />}
          {activeSection === 'classification-categories' && <ClassificationCategoriesSection />}
          {activeSection === 'statuts'       && <StatutsSection />}
          {activeSection === 'motifs'        && <MotifsSection />}
          {activeSection === 'ages'          && <AgesSection />}
          {activeSection === 'provisions'    && <ProvisionsSection />}
          {activeSection === 'notifications' && <NotificationsSection />}
          {activeSection === 'import-export' && <ImportExportSection />}
          {activeSection === 'ipm'           && <IpmSection />}
          {activeSection === 'prets-sogas'  && <PretsSogasSection />}
          {activeSection === 'paie-parametres' && <ParametresPaieSection />}
          {activeSection === 'paie-rubriques'  && <RubriquesPaieSection />}
          {activeSection === 'grille-salariale' && <GrilleSalarialeSection />}
          {activeSection === 'centre-validation' && <CentreValidationSection />}

        </div>
      </div>
    </Layout>
  );
}
